import { Modal } from "../team/ui.jsx";
const { useEffect, useMemo, useRef, useState } = React;
import { Api } from "../../platform/api.js";
import { Cap, bgUrl, normalizePhotoName, uploadCueImage } from "../data.jsx";
import { FIGHT_CARD_EXTRACT_PROMPT, FIGHT_CARD_HIDE_FOREVER_CODES, fightCardErrorMessage, fightCardUrlErrorMessage, normalizeFightCardRows } from "../fightcard.jsx";
import { useT } from "../i18n.jsx";
import { STAGE_PRESETS, STAGE_PRESET_BY_CODE, STAGE_PRESET_CATEGORIES, computeRowHeights, coveredRows, cumulative, findPhotoMatch, findStagePreset, getBlocksFromDay, layoutBlocks, masterCueLabel, detectBouts, dropCuesFromStage, stageRows, newBlock, newMasterCue, newStage, newStageFromPreset, parseBulkCueLine, planBulkLane, planBulkLaneByName, planBulkPaste, reindexAfterDelete, stageCueIds } from "../model.jsx";
import { usePhotoLibrary } from "../photoLibrary.jsx";
import { POOL_DRAG_TYPE, groupsOf, useCuePool } from "../cuePool.jsx";
import { blockForColumn, clearClipboard, clipRows, copyBlock, copyRow, copyRows, shiftForInsert, useCueClipboard } from "./cueClipboard.js";
import { COLORS, LayersIcon, Loader2, SearchIcon, Check, ClipboardPaste, Copy, Eye, EyeOff, GripVertical, ICONS, Icon, ImageIcon, Lock, Pencil, Plus, Redo2, Split, T, Trash2, Undo2, X, colorOf } from "../theme.jsx";

// ======================================================== SHARED BOARD =====

function BoardEditor({ board, onChange, perms, showMeta }) {
  const { t } = useT();
  const [newPlatformName, setNewPlatformName] = useState("");
  const [showAddPlatform, setShowAddPlatform] = useState(false);
  const historyRef = useRef({ past: [], future: [] });
  const commit = (next) => {
    historyRef.current.past.push(board);
    if (historyRef.current.past.length > 50) historyRef.current.past.shift();
    historyRef.current.future = [];
    onChange(next);
  };
  const undo = () => {
    const h = historyRef.current;
    if (h.past.length === 0) return;
    const prev = h.past.pop();
    h.future.push(board);
    onChange(prev);
  };
  const redo = () => {
    const h = historyRef.current;
    if (h.future.length === 0) return;
    const nxt = h.future.pop();
    h.past.push(board);
    onChange(nxt);
  };
  const canUndo = historyRef.current.past.length > 0;
  const canRedo = historyRef.current.future.length > 0;

  const getBlocks = (col) => getBlocksFromDay(board, col);
  const setBlocks = (col, blocks) => {
    if (col === "onGround") commit({ ...board, onGround: blocks });
    else if (col === "broadcastMain") commit({ ...board, broadcastMain: blocks });
    else commit({ ...board, platforms: { ...board.platforms, [col]: blocks } });
  };
  const addBlock = (col, start) => setBlocks(col, [...getBlocks(col), newBlock(start)]);
  const updateBlock = (col, id, patch) => setBlocks(col, getBlocks(col).map((b) => (b.id === id ? { ...b, ...patch } : b)));
  const deleteBlock = (col, id) => setBlocks(col, getBlocks(col).filter((b) => b.id !== id));
  const splitBlock = (col, id) => { const b = getBlocks(col).find((x) => x.id === id); if (b) setBlocks(col, [...getBlocks(col), newBlock(b.start)]); };
  // drag-and-drop reorder within a column: moving a block onto a target row
  // just re-points its `start` there (sharing a row with whatever else is
  // already on it works the same way `splitBlock` already does today).
  const moveBlockToStart = (col, id, targetStart) => {
    const blocks = getBlocks(col);
    const blk = blocks.find((b) => b.id === id);
    if (!blk || blk.start === targetStart) return;
    setBlocks(col, blocks.map((b) => (b.id === id ? { ...b, start: targetStart, span: 1 } : b)));
  };

  // ---- copy / paste cues between boards (see cueClipboard.js) ----
  const clip = useCueClipboard();
  const boardLabel = board.name || board.code || "";
  const pasteBlockAt = (col, start) => {
    const src = blockForColumn(clip, col);
    if (src) setBlocks(col, [...getBlocks(col), { ...newBlock(start), ...src }]);
  };
  const pasteIntoBlock = (col, id) => {
    const src = blockForColumn(clip, col);
    if (src) updateBlock(col, id, src);
  };
  // insert the copied row(s) (master cues + their blocks) at `idx`; idx === length appends
  const pastedRows = clipRows(clip);
  const pasteRowAt = (idx) => {
    if (!pastedRows || !pastedRows.length) return;
    const n = pastedRows.length;
    const at = Math.max(0, Math.min(idx, board.masterCues.length));
    const masterCues = [...board.masterCues];
    masterCues.splice(at, 0, ...pastedRows.map((r) => ({ ...newMasterCue(), ...r.cue })));
    const added = (col) => pastedRows.flatMap((r, k) => (r.blocks[col] || []).map((b) => ({ ...newBlock(at + k), ...b, start: at + k, span: Math.max(1, Math.min(b.span || 1, n - k)) })));
    const withRow = (col, list) => [...shiftForInsert(list, at, n), ...added(col)];
    const platforms = Object.fromEntries(Object.entries(board.platforms).map(([k, v]) => [k, withRow(k, v)]));
    // a platform column the copied rows had but this board doesn't: add it
    for (const r of pastedRows) for (const col of Object.keys(r.blocks)) {
      if (col !== "onGround" && col !== "broadcastMain" && !platforms[col]) platforms[col] = withRow(col, []);
    }
    commit({ ...board, masterCues, onGround: withRow("onGround", board.onGround), broadcastMain: withRow("broadcastMain", board.broadcastMain), platforms });
  };
  const clipLabel = clip ? (clip.kind === "rows" ? `${clip.rows.length} ${t("cuesUnit")}: ${clip.rows.map((r) => r.cue.name).filter(Boolean).slice(0, 3).join(", ")}${clip.rows.length > 3 ? "…" : ""}`
    : clip.kind === "row" ? clip.cue.name : (clip.block.image || clip.block.sound)) : "";

  // ---- multi-select master cues: copy / delete / drag as a group ----
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const lastPickRef = useRef(null);
  const liveSelected = board.masterCues.filter((m) => selectedIds.has(m.id)).map((m) => m.id);
  const toggleSelect = (id, range) => {
    const ids = board.masterCues.map((m) => m.id);
    const next = new Set(liveSelected);
    if (range && lastPickRef.current && ids.includes(lastPickRef.current)) {
      const a = ids.indexOf(lastPickRef.current), b = ids.indexOf(id);
      for (let i = Math.min(a, b); i <= Math.max(a, b); i++) next.add(ids[i]);
    } else if (next.has(id)) next.delete(id); else next.add(id);
    lastPickRef.current = id;
    setSelectedIds(next);
  };
  const clearSelection = () => { setSelectedIds(new Set()); lastPickRef.current = null; };
  const selectedIndices = () => board.masterCues.map((m, i) => (selectedIds.has(m.id) ? i : -1)).filter((i) => i >= 0);
  const copySelected = () => copyRows(board, selectedIndices(), boardLabel);
  const deleteSelected = () => {
    const del = new Set(liveSelected);
    if (!del.size) return;
    let { onGround, broadcastMain } = board;
    let platforms = board.platforms;
    // delete bottom-up so each index is still valid when its turn comes
    for (const idx of selectedIndices().sort((a, b) => b - a)) {
      onGround = reindexAfterDelete(onGround, idx);
      broadcastMain = reindexAfterDelete(broadcastMain, idx);
      platforms = Object.fromEntries(Object.entries(platforms).map(([k, v]) => [k, reindexAfterDelete(v, idx)]));
    }
    commit({
      ...board, onGround, broadcastMain, platforms,
      masterCues: board.masterCues.filter((m) => !del.has(m.id)),
      stages: (board.stages || []).map((s) => dropCuesFromStage(s, del, board.masterCues)),
    });
    clearSelection();
  };

  const addMasterCue = () => commit({ ...board, masterCues: [...board.masterCues, newMasterCue()] });
  const updateMasterCue = (id, patch) => commit({ ...board, masterCues: board.masterCues.map((m) => (m.id === id ? { ...m, ...patch } : m)) });
  const deleteMasterCue = (id) => {
    const idx = board.masterCues.findIndex((m) => m.id === id);
    if (idx === -1) return;
    commit({
      ...board,
      masterCues: board.masterCues.filter((m) => m.id !== id),
      onGround: reindexAfterDelete(board.onGround, idx),
      broadcastMain: reindexAfterDelete(board.broadcastMain, idx),
      platforms: Object.fromEntries(Object.entries(board.platforms).map(([k, v]) => [k, reindexAfterDelete(v, idx)])),
      // a deleted cue can't stay linked from a stage: a range shrinks past it,
      // a listed id is dropped — never a dangling reference
      stages: (board.stages || []).map((s) => dropCuesFromStage(s, new Set([id]), board.masterCues)),
    });
  };
  // drag-and-drop reorder, arbitrary distance: move `draggedId` to sit right
  // where `targetId` currently is. Blocks reference master cues by array
  // POSITION (`start`), not by stable id, so every block's `start` has to be
  // remapped through the resulting permutation — not just a 2-index swap.
  // Dragging one of several selected cues moves the whole selection (in its
  // current order) to the drop target.
  const reorderMasterCue = (draggedId, targetId) => {
    if (!draggedId || draggedId === targetId) return;
    const fromIdx = board.masterCues.findIndex((m) => m.id === draggedId);
    const toIdx = board.masterCues.findIndex((m) => m.id === targetId);
    if (fromIdx === -1 || toIdx === -1) return;
    const group = selectedIds.has(draggedId) && liveSelected.length > 1 ? new Set(liveSelected) : new Set([draggedId]);
    if (group.has(targetId)) return;
    const movedCues = board.masterCues.filter((m) => group.has(m.id));
    const masterCues = board.masterCues.filter((m) => !group.has(m.id));
    const firstIdx = board.masterCues.findIndex((m) => group.has(m.id));
    const tPos = masterCues.findIndex((m) => m.id === targetId);
    // moving down lands after the target, moving up lands before it
    masterCues.splice(toIdx > firstIdx ? tPos + 1 : tPos, 0, ...movedCues);
    const newIdxOf = new Map(masterCues.map((m, i) => [m.id, i]));
    const remapBlocks = (blocks) => blocks.map((b) => {
      const oldCue = board.masterCues[b.start];
      const newStart = oldCue ? newIdxOf.get(oldCue.id) : undefined;
      if (newStart === undefined || newStart === b.start) return b;
      // the row this block lands on may not have the same neighbors as
      // before, so a multi-row span across the old order no longer holds
      return { ...b, start: newStart, span: 1 };
    });
    commit({
      ...board, masterCues,
      onGround: remapBlocks(board.onGround),
      broadcastMain: remapBlocks(board.broadcastMain),
      platforms: Object.fromEntries(Object.entries(board.platforms).map(([k, v]) => [k, remapBlocks(v)])),
    });
  };

  const addStage = () => commit({ ...board, stages: [...(board.stages || []), newStage()] });
  const updateStage = (id, patch) => commit({ ...board, stages: (board.stages || []).map((s) => (s.id === id ? { ...s, ...patch } : s)) });
  const deleteStage = (id) => commit({ ...board, stages: (board.stages || []).filter((s) => s.id !== id) });
  // the bout brackets beside the master cue column
  const stageSpans = (board.stages || []).map((s) => ({ id: s.id, rows: stageRows(s, board.masterCues), label: s.stage || "•", title: [s.stage, s.waikruText, s.duringFightText].filter(Boolean).join(" · ") })).filter((sp) => sp.rows);
  // link every stage, in list order, to the bouts found in the rundown
  const [autoLinkAsk, setAutoLinkAsk] = useState(false);
  const bouts = useMemo(() => detectBouts(board.masterCues), [board.masterCues]);
  const stagesLinked = (board.stages || []).filter((s) => stageRows(s, board.masterCues) || stageCueIds(s).length).length;
  const autoLinkBouts = () => {
    const ids = board.masterCues.map((m) => m.id);
    commit({ ...board, stages: (board.stages || []).map((s, i) => (bouts[i] ? { ...s, cueRange: { from: ids[bouts[i].from], to: ids[bouts[i].to] }, assignedCueIds: [] } : s)) });
    setAutoLinkAsk(false);
  };

  // Wai Kru / During Fight template picker — RWS/RKO/Promoter reference presets.
  // Defaults to this board's own category when it matches one of the presets,
  // since that's almost always the template a user editing this board wants.
  const [stagePresetCat, setStagePresetCat] = useState(
    STAGE_PRESET_CATEGORIES.includes(STAGE_PRESET_BY_CODE[board.code]) ? STAGE_PRESET_BY_CODE[board.code]
      : STAGE_PRESET_CATEGORIES.includes(board.category) ? board.category : STAGE_PRESET_CATEGORIES[0]
  );
  const [stagePresetPos, setStagePresetPos] = useState("");
  const stagePresetList = STAGE_PRESETS[stagePresetCat] || [];
  const { list: photoList, applyFromLibrary } = usePhotoLibrary();
  // ---- cue pool: drag a pool item into a column to make a block ----
  const poolActions = useRef({});
  poolActions.current = {
    add: (col, start, src) => setBlocks(col, [...getBlocks(col), { ...newBlock(start), ...src }]),
    replace: (col, id, src) => updateBlock(col, id, src),
  };
  const blockFromPool = async (item) => {
    const photo = item.photo
      ? photoList.find((p) => normalizePhotoName(p.name) === normalizePhotoName(item.photo))
      : findPhotoMatch(item.name || "", photoList);
    let bg = "";
    if (photo) { try { bg = await applyFromLibrary(photo); } catch (e) {} }
    return { image: String(item.name || ""), sound: String(item.sound || ""), color: String(item.color || "warmgray"), ...(bg ? { bg } : {}) };
  };
  const dropPool = async (col, start, raw, replaceId) => {
    let item; try { item = JSON.parse(raw); } catch (e) { return; }
    if (!item || typeof item !== "object") return;
    const src = await blockFromPool(item);
    if (replaceId) poolActions.current.replace(col, replaceId, src); else poolActions.current.add(col, start, src);
  };

  // preset rows pick up their Wai Kru / During Fight photo from the Photo
  // Library when a library photo is named exactly like the scene
  const [stagePhotoBusy, setStagePhotoBusy] = useState(false);
  const withPresetPhotos = async (stages) => {
    const out = [];
    for (const st of stages) out.push({ ...st, ...(await presetPhotoPatch(st, photoList, applyFromLibrary)) });
    return out;
  };
  const addStageFromPreset = async () => {
    if (!stagePresetCat || !stagePresetPos || stagePhotoBusy) return;
    setStagePhotoBusy(true);
    try {
      const added = await withPresetPhotos([newStageFromPreset(stagePresetCat, stagePresetPos)]);
      commit({ ...board, stages: [...(board.stages || []), ...added] });
      setStagePresetPos("");
    } finally { setStagePhotoBusy(false); }
  };
  const loadAllStagePresets = async () => {
    if (!stagePresetCat || stagePresetList.length === 0 || stagePhotoBusy) return;
    setStagePhotoBusy(true);
    try {
      const added = await withPresetPhotos(stagePresetList.map((p) => newStageFromPreset(stagePresetCat, p.pos)));
      commit({ ...board, stages: [...(board.stages || []), ...added] });
    } finally { setStagePhotoBusy(false); }
  };

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  // where untargeted lines go: "append" (end), "insert" (before row bulkAt),
  // "replace" (overwrite rows from bulkAt down). Lines starting "#N" always
  // go to row N, whatever the mode.
  const [bulkMode, setBulkMode] = useState("append");
  const [bulkAt, setBulkAt] = useState(0);
  // which column the lines go into: "master" (master cues) or a block column
  // key — "onGround", "broadcastMain" or a platform name
  const [bulkTarget, setBulkTarget] = useState("master");
  const bulkTargets = [
    ...(perms.canMaster ? [{ key: "master", label: "MASTER CUE" }] : []),
    ...(perms.canOnGround ? [{ key: "onGround", label: "ON-GROUND" }] : []),
    ...(perms.canBroadcast ? [{ key: "broadcastMain", label: "BROADCAST" }, ...Object.keys(board.platforms).map((p) => ({ key: p, label: p }))] : []),
  ];
  const bulkIsLane = bulkTarget !== "master";
  const bulkParsed = useMemo(() => bulkText.split("\n").map(parseBulkCueLine).filter(Boolean), [bulkText]);
  const bulkMatchCount = useMemo(() => bulkParsed.filter((c) => findPhotoMatch(c.name, photoList)).length, [bulkParsed, photoList]);
  const bulkPlan = useMemo(() => planBulkPaste(bulkParsed, board.masterCues.length, bulkMode, bulkAt), [bulkParsed, board.masterCues.length, bulkMode, bulkAt]);
  // lane placement: "rows" = one line per row from bulkAt; "name" = match each
  // line's leading cue name to a master cue
  const [bulkLaneMode, setBulkLaneMode] = useState("rows");
  const bulkLanePlan = useMemo(() => bulkLaneMode === "name"
    ? { overflow: 0, missing: [], ...planBulkLaneByName(bulkParsed, board.masterCues) }
    : planBulkLane(bulkParsed, board.masterCues.length, bulkAt), [bulkLaneMode, bulkParsed, board.masterCues, bulkAt]);
  // "ai": the server asks Workers AI where each line goes; the answer is
  // shown as an editable list and nothing is placed until it's confirmed
  const [aiReview, setAiReview] = useState(null); // { lines, items: [{ key, line, row, text, on }] }, row 0-based or null
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState("");
  useEffect(() => { setAiReview(null); setAiError(""); }, [bulkText, bulkTarget, bulkLaneMode]);
  const runAiMatch = async () => {
    setAiBusy(true); setAiError("");
    try {
      const r = await Api.matchLane(board.masterCues.map((m) => String(m.name || "")), bulkText);
      // notes are in show order: a row before the previous note's (the model
      // matched a word from an earlier cue) moves to just after it, flagged
      let prev = -1;
      const items = r.placements.map((p, i) => {
        let row = p.row ? p.row - 1 : null;
        const moved = row !== null && row < prev;
        if (moved) row = Math.min(prev + 1, board.masterCues.length - 1);
        if (row !== null) prev = row;
        return { key: i, line: p.line, row, text: p.text, on: row !== null, moved };
      });
      setAiReview({ lines: r.lines, calls: r.calls, limit: r.limit, items });
    } catch (e) {
      const known = { daily_limit: "aiMatchDailyLimit", not_granted: "aiMatchNotSetUp", too_long: "aiMatchTooLong", forbidden: "aiMatchForbidden" };
      setAiError(t(known[e && e.code] || "aiMatchFailed"));
    } finally { setAiBusy(false); }
  };
  const setAiItem = (key, patch) => setAiReview((rv) => ({ ...rv, items: rv.items.map((it) => (it.key === key ? { ...it, ...patch } : it)) }));
  const aiPlaced = aiReview ? aiReview.items.filter((it) => it.on && it.row !== null && it.text.trim()) : [];
  const openBulk = () => { setBulkMode("append"); setBulkLaneMode("rows"); setBulkAt(0); setBulkTarget(bulkTargets[0] ? bulkTargets[0].key : "master"); setBulkOpen(true); };
  // block columns: "ภาพ | เสียง" — text before the first "|" is the block's
  // image text, after it the sound. A row that already has a block in this
  // column gets that block's text replaced; an empty row gets a new block.
  const applyBulkLane = () => {
    const col = bulkTarget;
    let blocks = [...getBlocks(col)];
    // AI review: lines that landed on the same row share its block
    const entries = bulkLaneMode === "ai"
      ? [...aiPlaced.reduce((m, it) => m.set(it.row, [...(m.get(it.row) || []), it.text.trim()]), new Map())].map(([row, texts]) => [row, { body: texts.join(" / "), marked: false }])
      : [...bulkLanePlan.place].map(([row, k]) => [row, { body: bulkLanePlan.text && bulkLanePlan.text.has(k) ? bulkLanePlan.text.get(k) : bulkParsed[k].name, marked: bulkParsed[k].marked, color: bulkParsed[k].color }]);
    for (const [row, c] of entries) {
      const body = c.body;
      const cut = body.indexOf("|");
      const image = (cut === -1 ? body : body.slice(0, cut)).trim();
      const sound = cut === -1 ? null : body.slice(cut + 1).trim();
      const patch = { image, ...(sound !== null ? { sound } : {}), ...(c.marked ? { color: c.color } : {}) };
      const at = blocks.findIndex((b) => b.start === row);
      if (at !== -1) blocks[at] = { ...blocks[at], ...patch };
      else blocks.push({ ...newBlock(row), sound: "", ...patch });
    }
    setBlocks(col, blocks);
    setBulkOpen(false);
    setBulkText("");
  };
  const applyBulkPaste = async () => {
    if (bulkBusy || bulkParsed.length === 0) return;
    if (bulkIsLane && bulkLaneMode === "ai") { if (aiPlaced.length) applyBulkLane(); return; }
    if (bulkIsLane) { if (bulkLanePlan.place.size) applyBulkLane(); return; }
    setBulkBusy(true);
    // sequential, not Promise.all — each match triggers a real upload (fetch +
    // re-upload a fresh copy of the library photo) and firing those in a burst
    // is exactly the kind of concurrent-call pattern that caused rate-limit
    // errors elsewhere in this app's storage layer.
    const bgs = [];
    for (const c of bulkParsed) {
      let bg = "";
      const match = findPhotoMatch(c.name, photoList);
      if (match) { try { bg = await applyFromLibrary(match); } catch (e) {} }
      bgs.push(bg);
    }
    const fields = (c, bg) => ({ name: c.name, color: c.color, icon: c.icon, boutNumber: c.boutNumber || "", ...(bg ? { bg } : {}) });
    // 1) replacements keep the row's id, blocks, icon and everything else on
    //    it; the text changes, plus the color only if the line had a marker,
    //    a bout number only if one was found, and a photo only if one matched
    let masterCues = board.masterCues.map((m, i) => {
      const k = bulkPlan.replace.get(i);
      if (k === undefined) return m;
      const c = bulkParsed[k];
      return { ...m, name: c.name, ...(c.marked ? { color: c.color } : {}), ...(c.boutNumber ? { boutNumber: c.boutNumber } : {}), ...(bgs[k] ? { bg: bgs[k] } : {}) };
    });
    // 2) one insert of all new rows at a single position; blocks below it move
    //    down by the same count so they stay on the cue they were on
    let next = board;
    const added = bulkPlan.add.map((k) => ({ ...newMasterCue(), bg: "", ...fields(bulkParsed[k], bgs[k]) }));
    if (added.length) {
      const at = bulkPlan.addAt;
      masterCues = [...masterCues.slice(0, at), ...added, ...masterCues.slice(at)];
      if (at < board.masterCues.length) {
        const shift = (list) => shiftForInsert(list, at, added.length);
        next = { ...board, onGround: shift(board.onGround), broadcastMain: shift(board.broadcastMain),
          platforms: Object.fromEntries(Object.entries(board.platforms).map(([k, v]) => [k, shift(v)])) };
      }
    }
    commit({ ...next, masterCues });
    setBulkBusy(false);
    setBulkOpen(false);
    setBulkText("");
  };

  // ---- fight-card image import: upload a poster, read it via `sample`,
  // match the returned bouts to this board's existing master cues by bout
  // number only (never auto-creating a cue), let the user eyeball the
  // reading before it's applied.
  const [fcOpen, setFcOpen] = useState(false);
  const [fcMode, setFcMode] = useState("image"); // "image" | "url"
  const [fcFiles, setFcFiles] = useState([]);
  const [fcUrl, setFcUrl] = useState("");
  const [fcAccept, setFcAccept] = useState("image/*");
  const [fcBusy, setFcBusy] = useState(false);
  const [fcError, setFcError] = useState("");
  const [fcDead, setFcDead] = useState(false); // hit a hide-forever error code this load
  const [fcParsed, setFcParsed] = useState(null); // [{boutNumber, redCorner, blueCorner}] once scanned
  // "Paste a link" mode only exists on self-hosted deploy targets (the
  // server fetches the page) — see Cap.sample.fromUrl in src/platform/api.js. On
  // the real claude.ai Artifact runtime this is undefined, so the tab stays
  // hidden and the feature behaves exactly as it always did there.
  const fcUrlAvailable = !!(Cap.sample && Cap.sample.fromUrl);

  const openFightCard = () => {
    setFcMode("image"); setFcFiles([]); setFcUrl(""); setFcParsed(null); setFcError(""); setFcOpen(true);
    if (Cap.sample) {
      Cap.sample.limits().then((limits) => {
        if (limits && limits.images) setFcAccept(limits.images.mediaTypes.join(","));
        else { setFcAccept(""); setFcDead(true); setFcError(t("fightCardErrUnavailable")); }
      }).catch(() => { setFcAccept(""); setFcDead(true); setFcError(t("fightCardErrUnavailable")); });
    } else {
      setFcDead(true); setFcError(t("fightCardErrUnavailable"));
    }
  };
  const closeFightCard = () => { if (!fcBusy) setFcOpen(false); };
  const scanFightCard = async () => {
    if (fcBusy || fcDead || fcFiles.length === 0 || !Cap.sample) { if (fcFiles.length === 0) setFcError(t("fightCardNoFile")); return; }
    setFcBusy(true); setFcError(""); setFcParsed(null);
    try {
      const rows = normalizeFightCardRows(await Cap.sample.json(FIGHT_CARD_EXTRACT_PROMPT, { images: fcFiles, modelTier: "default", cache: false }));
      if (rows.length === 0) setFcError(t("fightCardNoneFound"));
      setFcParsed(rows);
    } catch (e) {
      setFcParsed(null);
      setFcError(fightCardErrorMessage(e, t));
      if (FIGHT_CARD_HIDE_FOREVER_CODES.has(e && e.code)) setFcDead(true);
    } finally {
      setFcBusy(false);
    }
  };
  const scanFightCardUrl = async () => {
    const url = fcUrl.trim();
    if (fcBusy || fcDead || !url || !fcUrlAvailable) { if (!url) setFcError(t("fightCardNoUrl")); return; }
    setFcBusy(true); setFcError(""); setFcParsed(null);
    try {
      const rows = normalizeFightCardRows(await Cap.sample.fromUrl(url));
      if (rows.length === 0) setFcError(t("fightCardNoneFound"));
      setFcParsed(rows);
    } catch (e) {
      setFcParsed(null);
      setFcError(fightCardUrlErrorMessage(e, t));
      if (FIGHT_CARD_HIDE_FOREVER_CODES.has(e && e.code)) setFcDead(true);
    } finally {
      setFcBusy(false);
    }
  };
  // derived match against THIS board's current master cues, by bout number only
  const fcMatched = useMemo(() => {
    if (!fcParsed) return null;
    return fcParsed.map((row) => {
      const cue = board.masterCues.find((c) => c.boutNumber && String(c.boutNumber).trim() === row.boutNumber);
      return { ...row, cueId: cue ? cue.id : null, cueName: cue ? (cue.name || t("untitledCue")) : null };
    });
  }, [fcParsed, board.masterCues, t]);
  const fcMatchedCount = useMemo(() => (fcMatched || []).filter((r) => r.cueId).length, [fcMatched]);
  const updateFcRow = (idx, patch) => setFcParsed((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const applyFightCard = () => {
    if (!fcMatched) return;
    const patchMap = new Map(fcMatched.filter((r) => r.cueId).map((r) => [r.cueId, { redCorner: r.redCorner, blueCorner: r.blueCorner }]));
    if (patchMap.size > 0) {
      commit({ ...board, masterCues: board.masterCues.map((m) => (patchMap.has(m.id) ? { ...m, ...patchMap.get(m.id) } : m)) });
    }
    setFcOpen(false);
  };

  const addPlatform = () => {
    const name = newPlatformName.trim();
    if (!name || board.platforms[name]) { setShowAddPlatform(false); setNewPlatformName(""); return; }
    commit({ ...board, platforms: { ...board.platforms, [name]: [] } });
    setNewPlatformName(""); setShowAddPlatform(false);
  };
  const removePlatform = (name) => {
    const p = { ...board.platforms }; delete p[name];
    commit({ ...board, platforms: p });
  };

  const [hidden, setHidden] = useState(() => new Set());
  const toggleHidden = (key) => setHidden((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });

  const allBroadcastColumns = useMemo(() => [board.broadcastMain, ...Object.values(board.platforms)], [board.broadcastMain, board.platforms]);
  const heights = useMemo(() => computeRowHeights(board.masterCues.length, [board.onGround, ...allBroadcastColumns]), [board.masterCues.length, board.onGround, allBroadcastColumns]);
  const cum = useMemo(() => cumulative(heights), [heights]);
  const totalHeight = cum[cum.length - 1] || 0;
  const platformNames = Object.keys(board.platforms);

  const columnDefs = [
    { key: "onGround", label: "ON-GROUND", editable: perms.canOnGround, removable: false },
    { key: "broadcastMain", label: "BROADCAST", editable: perms.canBroadcast, removable: false },
    ...platformNames.map((p) => ({ key: p, label: p, editable: perms.canBroadcast, removable: true })),
  ];
  const visibleDefs = columnDefs.filter((c) => !hidden.has(c.key));
  const MASTER_W = 190, COL_W = 230;

  return (
    <div style={{ padding: 14, textAlign: "center" }}>
      <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 10 }}>
        <button className="rb-btn" onClick={undo} disabled={!canUndo} title={t("undo")}
          style={{ display: "flex", alignItems: "center", gap: 5, background: T.panel2, border: `1px solid ${T.border}`, borderRadius: 4, padding: "6px 11px", fontSize: 12, fontWeight: 700, color: canUndo ? T.text : T.subtext, cursor: canUndo ? "pointer" : "default", opacity: canUndo ? 1 : 0.5 }}>
          <Undo2 size={13} /> {t("undo")}
        </button>
        <button className="rb-btn" onClick={redo} disabled={!canRedo} title={t("redo")}
          style={{ display: "flex", alignItems: "center", gap: 5, background: T.panel2, border: `1px solid ${T.border}`, borderRadius: 4, padding: "6px 11px", fontSize: 12, fontWeight: 700, color: canRedo ? T.text : T.subtext, cursor: canRedo ? "pointer" : "default", opacity: canRedo ? 1 : 0.5 }}>
          <Redo2 size={13} /> {t("redo")}
        </button>
      </div>
      {clip && (
        <div role="status" data-cue-clipboard style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, flexWrap: "wrap", margin: "0 auto 10px", maxWidth: 640, padding: "7px 10px", background: T.panel2, border: `1px dashed ${T.border}`, borderRadius: 8, fontSize: 12, color: T.text }}>
          <ClipboardPaste size={14} />
          <span style={{ fontWeight: 700 }}>{clip.kind === "rows" ? t("clipboardRows") : clip.kind === "row" ? t("clipboardRow") : t("clipboardBlock")}: “{(clipLabel || t("untitledBlock")).split("\n")[0].slice(0, 80)}”</span>
          {clip.from && <span style={{ color: T.subtext }}>{t("clipboardFrom")} {clip.from}</span>}
          <span title={clip.kind !== "block" ? t("clipboardHintRow") : t("clipboardHintBlock")} aria-label={clip.kind !== "block" ? t("clipboardHintRow") : t("clipboardHintBlock")} style={{ cursor: "help", color: T.subtext, border: `1px solid ${T.border}`, borderRadius: 10, width: 18, height: 18, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800 }}>?</span>
          <button className="rb-btn" onClick={clearClipboard} aria-label={t("clipboardClear")} title={t("clipboardClear")}
            style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 4, padding: "2px 6px", cursor: "pointer", color: T.subtext, display: "flex", alignItems: "center", gap: 3, fontSize: 11 }}><X size={11} /> {t("clipboardClear")}</button>
        </div>
      )}
      {liveSelected.length > 0 && (
        <div role="toolbar" aria-label={t("selectionBar")} data-cue-selection style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, flexWrap: "wrap", margin: "0 auto 10px", maxWidth: 640, padding: "7px 10px", background: T.panel2, border: `1.5px solid ${T.accent}`, borderRadius: 8, fontSize: 12, color: T.text }}>
          <span style={{ fontWeight: 800 }}>{t("selectedCount").replace("{n}", liveSelected.length)}</span>
          <button className="rb-btn" onClick={copySelected} style={{ display: "flex", alignItems: "center", gap: 4, background: T.accent, color: T.accentText, border: "none", borderRadius: 6, padding: "5px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}><Copy size={12} /> {t("copySelected")}</button>
          {perms.canMaster && <button className="rb-btn" onClick={deleteSelected} style={{ display: "flex", alignItems: "center", gap: 4, background: T.panel, color: T.danger, border: `1px solid ${T.border}`, borderRadius: 6, padding: "5px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}><Trash2 size={12} /> {t("deleteSelected")}</button>}
          <button className="rb-btn" onClick={clearSelection} style={{ display: "flex", alignItems: "center", gap: 4, background: "none", color: T.subtext, border: `1px solid ${T.border}`, borderRadius: 6, padding: "5px 10px", fontSize: 12, cursor: "pointer" }}><X size={12} /> {t("clearSelection")}</button>
          <span title={t("selectionHint")} aria-label={t("selectionHint")} style={{ cursor: "help", color: T.subtext, border: `1px solid ${T.border}`, borderRadius: 10, width: 18, height: 18, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800 }}>?</span>
        </div>
      )}
      {showMeta && (
        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", justifyContent: "center" }}>
          <input aria-label={t("showNamePh")} className="rb-input" placeholder={t("showNamePh")} value={board.name} disabled={!perms.canMaster}
            onChange={(e) => commit({ ...board, name: e.target.value })}
            style={{ flex: "1 1 160px", maxWidth: 260, fontSize: 13, fontWeight: 700, border: `1px solid ${T.border}`, borderRadius: 10, padding: "8px 10px", background: T.panel2, color: T.text, textAlign: "center" }} />
          <input aria-label="Category" className="rb-input" placeholder="Category" value={board.category} disabled={!perms.canMaster}
            onChange={(e) => commit({ ...board, category: e.target.value })}
            style={{ flex: "0 1 130px", fontSize: 12.5, border: `1px solid ${T.border}`, borderRadius: 10, padding: "8px 10px", background: T.panel2, color: T.text, textAlign: "center" }} />
          <input aria-label="Code" className="rb-input" placeholder="Code" value={board.code} disabled={!perms.canMaster}
            onChange={(e) => commit({ ...board, code: e.target.value })}
            style={{ flex: "0 1 130px", fontSize: 12.5, border: `1px solid ${T.border}`, borderRadius: 10, padding: "8px 10px", background: T.panel2, color: T.text, textAlign: "center" }} />
        </div>
      )}

      <div className="rb-scroll" style={{ display: "flex", gap: 6, overflowX: "auto", marginBottom: 10, justifyContent: "center" }}>
        <span style={{ flex: `0 0 ${MASTER_W}px`, fontSize: 11, color: T.subtext }}>{t("alwaysShown")}</span>
        {columnDefs.map((c) => (
          <ColumnChip key={c.key} label={c.label} visible={!hidden.has(c.key)} onToggle={() => toggleHidden(c.key)}
            onRemove={c.removable && perms.canBroadcast ? () => removePlatform(c.key) : null} />
        ))}
        {perms.canBroadcast && (
          showAddPlatform ? (
            <span style={{ display: "flex", gap: 4 }}>
              <input aria-label={t("platformNamePh")} className="rb-input" autoFocus value={newPlatformName} onChange={(e) => setNewPlatformName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addPlatform(); if (e.key === "Escape") setShowAddPlatform(false); }}
                placeholder={t("platformNamePh")} style={{ fontSize: 12, padding: "6px 8px", border: `1px solid ${T.accent}`, borderRadius: 8, width: 110, background: T.panel2, color: T.text, textAlign: "center" }} />
              <button aria-label={t("addPlatform")} className="rb-btn" onClick={addPlatform} style={{ background: T.accent, color: T.accentText, border: "none", borderRadius: 8, padding: "6px 9px", cursor: "pointer" }}><Plus size={13} /></button>
            </span>
          ) : (
            <button className="rb-btn" onClick={() => setShowAddPlatform(true)} style={{ display: "flex", alignItems: "center", gap: 4, background: "none", border: `1px dashed ${T.border}`, borderRadius: 8, padding: "6px 10px", fontSize: 11.5, color: T.subtext, cursor: "pointer", whiteSpace: "nowrap" }}>
              <Plus size={11} /> {t("addPlatform")}
            </button>
          )
        )}
      </div>

      {(perms.canOnGround || perms.canBroadcast) && <PoolPanel />}

      <div className="rb-scroll" style={{ overflowX: "auto", paddingBottom: 6 }}>
        <div style={{ display: "flex", gap: 8, justifyContent: "center", minWidth: "max-content" }}>
          {stageSpans.length > 0 && <div style={{ flex: "0 0 22px" }} />}
          <div style={{ flex: `0 0 ${MASTER_W}px`, width: MASTER_W, textAlign: "center", fontSize: 11.5, fontWeight: 800, color: T.text, letterSpacing: 0.4 }}>MASTER CUE</div>
          {visibleDefs.map((c) => (
            <div key={c.key} style={{ flex: `0 0 ${COL_W}px`, width: COL_W, textAlign: "center", fontSize: 11.5, fontWeight: 800, color: T.text, letterSpacing: 0.4 }}>{c.label.toUpperCase()}</div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "center", minWidth: "max-content", marginTop: 6 }}>
          {stageSpans.length > 0 && (
            <div data-bout-gutter aria-hidden="true" style={{ flex: "0 0 22px", width: 22, position: "relative", height: totalHeight }}>
              {stageSpans.map((sp, k) => (
                <div key={sp.id} data-bout-span={sp.label} title={sp.title}
                  style={{ position: "absolute", top: cum[sp.rows[0]] + 4, height: cum[sp.rows[1]] + heights[sp.rows[1]] - cum[sp.rows[0]] - 8, left: 3, width: 16, borderRadius: 8, background: k % 2 ? T.accentInk : T.accent, color: k % 2 ? "#fff" : T.accentText, display: "flex", alignItems: "flex-start", justifyContent: "center", overflow: "hidden" }}>
                  <span style={{ position: "sticky", top: 8, marginTop: 8, writingMode: "vertical-rl", fontSize: 10.5, fontWeight: 900, letterSpacing: 0.5, whiteSpace: "nowrap" }}>{sp.label}</span>
                </div>
              ))}
            </div>
          )}
          <div className={liveSelected.length ? "rb-selecting" : ""} style={{ flex: `0 0 ${MASTER_W}px`, width: MASTER_W, position: "relative", height: totalHeight }}>
            {board.masterCues.map((m, i) => (
              <MasterBox key={m.id} cue={m} top={cum[i]} height={heights[i]} editable={perms.canMaster}
                selected={selectedIds.has(m.id)} onToggleSelect={(range) => toggleSelect(m.id, range)}
                onReorder={(draggedId) => reorderMasterCue(draggedId, m.id)}
                onUpdate={(patch) => updateMasterCue(m.id, patch)} onDelete={() => deleteMasterCue(m.id)}
                onCopy={() => copyRow(board, i, boardLabel)}
                onPasteRowAfter={pastedRows ? () => pasteRowAt(i + 1) : null} />
            ))}
          </div>
          {visibleDefs.map((c) => (
            <BlockColumn key={c.key} width={`${COL_W}px`} col={c.key} blocks={getBlocks(c.key)} masterLen={board.masterCues.length} heights={heights} cum={cum}
              editable={c.editable} onAdd={addBlock} onUpdate={updateBlock} onDelete={deleteBlock} onSplit={splitBlock} onMove={moveBlockToStart}
              onPoolDrop={dropPool}
              canPaste={!!blockForColumn(clip, c.key)} onPaste={pasteBlockAt} onPasteInto={pasteIntoBlock} onCopy={(b) => copyBlock(b, boardLabel)} />
          ))}
        </div>
      </div>

      {perms.canMaster && (
        <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap", marginTop: 10 }}>
          <button className="rb-btn" onClick={addMasterCue} style={{ width: MASTER_W, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 0", background: T.panel2, border: `1.5px dashed ${T.border}`, borderRadius: 12, color: T.accentInk, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
            <Plus size={14} /> {t("addMasterCue")}
          </button>
          {pastedRows && (
            <button className="rb-btn" onClick={() => pasteRowAt(board.masterCues.length)} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 14px", background: T.panel2, border: `1.5px dashed ${T.border}`, borderRadius: 12, color: T.accentInk, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
              <ClipboardPaste size={14} /> {t("pasteRowEnd")}
            </button>
          )}
          <button className="rb-btn" onClick={openBulk} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 14px", background: T.panel2, border: `1.5px dashed ${T.border}`, borderRadius: 12, color: T.text, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
            <Copy size={14} /> {t("bulkPasteBtn")}
          </button>
          {Cap.sample && (
            <button className="rb-btn" onClick={openFightCard} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 14px", background: T.panel2, border: `1.5px dashed ${T.border}`, borderRadius: 12, color: T.text, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
              <ImageIcon size={14} /> {t("fightCardBtn")}
            </button>
          )}
        </div>
      )}
      {!perms.canMaster && (perms.canOnGround || perms.canBroadcast) && board.masterCues.length > 0 && (
        <div style={{ display: "flex", justifyContent: "center", marginTop: 10 }}>
          <button className="rb-btn" onClick={openBulk} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 14px", background: T.panel2, border: `1.5px dashed ${T.border}`, borderRadius: 12, color: T.text, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
            <Copy size={14} /> {t("bulkPasteBtn")}
          </button>
        </div>
      )}
      <div style={{ marginTop: 14, fontSize: 11, color: T.subtext, textAlign: "center" }}>{t("sharedDataNote")}</div>

      {perms.canMaster && (
        <div style={{ marginTop: 26, borderTop: `1px solid ${T.border}`, paddingTop: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: T.text, marginBottom: 4 }}>{t("stagesTitle")}</div>
          <div style={{ fontSize: 11, color: T.subtext, marginBottom: 12, maxWidth: 620, marginLeft: "auto", marginRight: "auto", whiteSpace: "pre-line" }}>{t("stagesDesc")}</div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", justifyContent: "center", marginBottom: 14, maxWidth: 620, marginLeft: "auto", marginRight: "auto" }}>
            <select aria-label={t("settingsStagePresets")} value={stagePresetCat} onChange={(e) => { setStagePresetCat(e.target.value); setStagePresetPos(""); }}
              style={{ fontSize: 11.5, fontWeight: 700, border: `1px solid ${T.border}`, borderRadius: 6, padding: "6px 8px", background: "#fff", color: T.text }}>
              {STAGE_PRESET_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select aria-label={t("stageLabelPh")} value={stagePresetPos} onChange={(e) => setStagePresetPos(e.target.value)}
              style={{ fontSize: 11.5, border: `1px solid ${T.border}`, borderRadius: 6, padding: "6px 8px", background: "#fff", color: T.text, minWidth: 180 }}>
              <option value="">— {t("stageLabelPh")} —</option>
              {stagePresetList.map((p) => <option key={p.pos} value={p.pos}>{p.pos} · {p.waikruText} / {p.duringFightText}</option>)}
            </select>
            <button className="rb-btn" onClick={addStageFromPreset} disabled={!stagePresetPos || stagePhotoBusy}
              style={{ display: "flex", alignItems: "center", gap: 5, background: T.accent, color: T.accentText, border: "none", borderRadius: 6, padding: "6px 10px", fontSize: 11.5, fontWeight: 700, cursor: stagePresetPos ? "pointer" : "default", opacity: stagePresetPos ? 1 : 0.5 }}>
              <Plus size={12} /> {t("addStageFromTemplateBtn")}
            </button>
            <button className="rb-btn" onClick={loadAllStagePresets}
              style={{ display: "flex", alignItems: "center", gap: 5, background: T.panel2, color: T.text, border: `1px solid ${T.border}`, borderRadius: 6, padding: "6px 10px", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>
              <Copy size={12} /> {t("loadAllPositionsBtn")}
            </button>
          </div>
          <div style={{ fontSize: 11, color: T.subtext, marginBottom: 12, maxWidth: 560, marginLeft: "auto", marginRight: "auto" }}>{t("templatePickerHint")}</div>

          {(board.stages || []).length === 0 && (
            <div style={{ fontSize: 12, color: T.subtext, marginBottom: 10 }}>{t("stagesEmpty")}</div>
          )}
          {(board.stages || []).length > 0 && (
            <div data-auto-link style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", justifyContent: "center", marginBottom: 12, maxWidth: 760, marginLeft: "auto", marginRight: "auto", fontSize: 11.5, color: T.text }}>
              <span>{bouts.length ? t("boutsFound").replace("{n}", bouts.length).replace("{list}", bouts.map((b) => `${b.from + 1}–${b.to + 1}`).join(", ")) : t("boutsNone")}</span>
              {bouts.length > 0 && (autoLinkAsk ? (
                <>
                  <span style={{ fontWeight: 700, color: T.accentInk }}>{t("autoLinkConfirm").replace("{n}", stagesLinked)}</span>
                  <button className="rb-btn" onClick={autoLinkBouts} style={{ background: T.accent, color: T.accentText, border: "none", borderRadius: 6, padding: "6px 10px", fontSize: 11.5, fontWeight: 800, cursor: "pointer" }}>{t("autoLinkYes")}</button>
                  <button className="rb-btn" onClick={() => setAutoLinkAsk(false)} style={{ background: "none", color: T.text, border: `1px solid ${T.border}`, borderRadius: 6, padding: "6px 10px", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>{t("cancel")}</button>
                </>
              ) : (
                <button className="rb-btn" onClick={() => (stagesLinked ? setAutoLinkAsk(true) : autoLinkBouts())}
                  style={{ display: "flex", alignItems: "center", gap: 5, background: T.accent, color: T.accentText, border: "none", borderRadius: 6, padding: "6px 10px", fontSize: 11.5, fontWeight: 800, cursor: "pointer" }}>
                  <LayersIcon size={12} /> {t("autoLinkBtn")}
                </button>
              ))}
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 760, marginLeft: "auto", marginRight: "auto" }}>
            {(board.stages || []).map((s) => (
              <StageRow key={s.id} stage={s} masterCues={board.masterCues}
                onUpdate={(patch) => updateStage(s.id, patch)} onDelete={() => deleteStage(s.id)} />
            ))}
          </div>
          <button className="rb-btn" onClick={addStage} style={{ marginTop: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 14px", background: T.panel2, border: `1.5px dashed ${T.border}`, borderRadius: 10, color: T.accentInk, fontSize: 12.5, fontWeight: 700, cursor: "pointer", marginLeft: "auto", marginRight: "auto" }}>
            <Plus size={13} /> {t("addStageBtn")}
          </button>
        </div>
      )}

      {bulkOpen && (
        <Modal title={t("bulkPasteTitle")} onClose={() => !bulkBusy && !aiBusy && setBulkOpen(false)} closeLabel={t("cancel")} width={600}>
            <div style={{ fontSize: 11, color: T.subtext, marginBottom: 10, whiteSpace: "pre-line" }}>{t("bulkPasteHint")}</div>
            <textarea aria-label={t("bulkPasteTextareaPh")} autoFocus rows={10} value={bulkText} onChange={(e) => setBulkText(e.target.value)} placeholder={t("bulkPasteTextareaPh")}
              style={{ width: "100%", boxSizing: "border-box", fontSize: 12.5, border: `1px solid ${T.border}`, borderRadius: 8, padding: 10, background: "#fff", color: T.text, fontFamily: "inherit", resize: "vertical" }} />
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginTop: 10, fontSize: 12, color: T.text }}>
              <span style={{ fontWeight: 700 }}>{t("bulkTargetLabel")}</span>
              <select aria-label={t("bulkTargetLabel")} value={bulkTarget} onChange={(e) => setBulkTarget(e.target.value)}
                style={{ fontSize: 12, fontWeight: 700, border: `1px solid ${T.border}`, borderRadius: 6, padding: "5px 8px", background: "#fff", color: T.text }}>
                {bulkTargets.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
            </div>
            {bulkIsLane && <div style={{ fontSize: 11, color: T.subtext, marginTop: 6 }}>{bulkLaneMode === "ai" ? t("bulkLaneAiHint") : bulkLaneMode === "name" ? t("bulkLaneNameHint") : t("bulkLaneHint")}</div>}
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginTop: 8, fontSize: 12, color: T.text }}>
              <span style={{ fontWeight: 700 }}>{t("bulkPlaceLabel")}</span>
              {bulkIsLane && <select aria-label={t("bulkLaneModeLabel")} value={bulkLaneMode} onChange={(e) => setBulkLaneMode(e.target.value)}
                style={{ fontSize: 12, border: `1px solid ${T.border}`, borderRadius: 6, padding: "5px 8px", background: "#fff", color: T.text }}>
                <option value="rows">{t("bulkLaneByRows")}</option>
                <option value="name">{t("bulkLaneByName")}</option>
                <option value="ai">{t("bulkLaneByAi")}</option>
              </select>}
              {!bulkIsLane && <select aria-label={t("bulkPlaceLabel")} value={bulkMode} onChange={(e) => setBulkMode(e.target.value)}
                style={{ fontSize: 12, border: `1px solid ${T.border}`, borderRadius: 6, padding: "5px 8px", background: "#fff", color: T.text }}>
                <option value="append">{t("bulkPlaceAppend")}</option>
                {board.masterCues.length > 0 && <option value="insert">{t("bulkPlaceInsert")}</option>}
                {board.masterCues.length > 0 && <option value="replace">{t("bulkPlaceReplace")}</option>}
              </select>}
              {((bulkIsLane && bulkLaneMode === "rows") || (!bulkIsLane && bulkMode !== "append")) && board.masterCues.length > 0 && (
                <select aria-label={t("bulkPlaceRow")} value={bulkAt} onChange={(e) => setBulkAt(Number(e.target.value))}
                  style={{ fontSize: 12, border: `1px solid ${T.border}`, borderRadius: 6, padding: "5px 8px", background: "#fff", color: T.text, maxWidth: 300 }}>
                  {board.masterCues.map((m, i) => (
                    <option key={m.id} value={i}>{(i + 1) + ". " + (String(m.name || "").split("\n")[0].slice(0, 40) || "—")}</option>
                  ))}
                </select>
              )}
            </div>
            <div style={{ fontSize: 11, color: T.subtext, marginTop: 6 }}>
              {bulkParsed.length > 0
                ? t("bulkPasteSummary").replace("{n}", bulkParsed.length).replace("{m}", bulkMatchCount)
                : t("bulkPasteEmptyWarn")}
            </div>
            {bulkIsLane && bulkLaneMode === "ai" && aiError && (
              <div role="alert" data-ai-error style={{ fontSize: 12, fontWeight: 700, color: T.danger, background: T.dangerBg, borderRadius: 6, padding: "6px 8px", marginTop: 8 }}>{aiError}</div>
            )}
            {bulkIsLane && bulkLaneMode === "ai" && aiReview && (() => {
              let prevRow = null;
              return (
                <>
                  <div data-bulk-plan style={{ fontSize: 11, color: T.text, marginTop: 8 }}>
                    {t("aiMatchSummary").replace("{n}", aiPlaced.length).replace("{m}", new Set(aiPlaced.map((it) => it.row)).size).replace("{c}", aiReview.calls).replace("{l}", aiReview.limit)}
                  </div>
                  <div data-ai-review className="rb-scroll" style={{ marginTop: 6, maxHeight: 320, overflowY: "auto", border: `1px solid ${T.border}`, borderRadius: 8, background: "#fff" }}>
                    {aiReview.items.map((it) => {
                      const placed = it.on && it.row !== null;
                      const back = placed && prevRow !== null && it.row < prevRow;
                      if (placed) prevRow = it.row;
                      return (
                        <div key={it.key} data-ai-item={it.line} style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", padding: "6px 8px", borderBottom: `1px solid ${T.border}`, background: back || it.moved ? T.warnBg : "transparent", opacity: placed ? 1 : 0.6 }}>
                          <input type="checkbox" aria-label={`${t("aiMatchUse")} ${it.line}`} checked={placed} disabled={it.row === null} onChange={(e) => setAiItem(it.key, { on: e.target.checked })} />
                          <span title={aiReview.lines[it.line - 1]} style={{ fontSize: 10.5, fontWeight: 800, color: T.subtext, width: 22 }}>{it.line}</span>
                          <input aria-label={`${t("aiMatchText")} ${it.line}`} className="rb-input" value={it.text} onChange={(e) => setAiItem(it.key, { text: e.target.value })}
                            style={{ flex: "2 1 200px", minWidth: 0, fontSize: 12, border: `1px solid ${T.border}`, borderRadius: 6, padding: "4px 6px", background: T.panel, color: T.text }} />
                          <select aria-label={`${t("aiMatchRow")} ${it.line}`} value={it.row === null ? "" : it.row} onChange={(e) => setAiItem(it.key, e.target.value === "" ? { row: null, on: false, moved: false } : { row: Number(e.target.value), on: true, moved: false })}
                            style={{ flex: "1 1 170px", maxWidth: 260, minWidth: 0, fontSize: 12, border: `1px solid ${T.border}`, borderRadius: 6, padding: "4px 6px", background: T.panel, color: T.text }}>
                            <option value="">{t("aiMatchNoRow")}</option>
                            {board.masterCues.map((m, i) => <option key={m.id} value={i}>{(i + 1) + ". " + (String(m.name || "").split("\n")[0].slice(0, 40) || "—")}</option>)}
                          </select>
                          {(back || it.moved) && <span style={{ fontSize: 10.5, fontWeight: 800, color: T.warnText }}>{t(it.moved ? "aiMatchMoved" : "aiMatchBackwards")}</span>}
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()}
            {bulkParsed.length > 0 && bulkIsLane && bulkLaneMode !== "ai" && (
              <div data-bulk-plan style={{ fontSize: 11, color: T.text, marginTop: 4 }}>
                {[
                  bulkLanePlan.place.size > 0 && t("bulkLanePlan").replace("{col}", (bulkTargets.find((o) => o.key === bulkTarget) || {}).label || bulkTarget).replace("{rows}", [...bulkLanePlan.place.keys()].map((i) => i + 1).join(", ")),
                  bulkLanePlan.overflow > 0 && t("bulkLaneOverflow").replace("{n}", bulkLanePlan.overflow),
                  bulkLanePlan.missing.length > 0 && t("bulkLaneMissing").replace("{rows}", bulkLanePlan.missing.join(", ")),
                  bulkLanePlan.unmatched && bulkLanePlan.unmatched.length > 0 && t("bulkLaneUnmatched").replace("{names}", bulkLanePlan.unmatched.slice(0, 5).join(", ") + (bulkLanePlan.unmatched.length > 5 ? "…" : "")),
                  bulkLanePlan.noSep > 0 && t("bulkLaneNoSep").replace("{n}", bulkLanePlan.noSep),
                ].filter(Boolean).join(" · ")}
              </div>
            )}
            {bulkParsed.length > 0 && !bulkIsLane && (
              <div data-bulk-plan style={{ fontSize: 11, color: T.text, marginTop: 4 }}>
                {[
                  bulkPlan.replace.size > 0 && t("bulkPlanReplace").replace("{rows}", [...bulkPlan.replace.keys()].map((i) => i + 1).join(", ")),
                  bulkPlan.add.length > 0 && (bulkPlan.addAt >= board.masterCues.length
                    ? t("bulkPlanAppend").replace("{n}", bulkPlan.add.length)
                    : t("bulkPlanInsert").replace("{n}", bulkPlan.add.length).replace("{row}", bulkPlan.addAt + 1)),
                  bulkPlan.missing.length > 0 && t("bulkPlanMissing").replace("{rows}", bulkPlan.missing.join(", ")),
                ].filter(Boolean).join(" · ")}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
              <button className="rb-btn" onClick={() => setBulkOpen(false)} disabled={bulkBusy || aiBusy}
                style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 14px", color: T.text, fontSize: 12.5, fontWeight: 700, cursor: bulkBusy ? "default" : "pointer" }}>
                {t("cancel")}
              </button>
              {bulkIsLane && bulkLaneMode === "ai" && (
                <button className="rb-btn" onClick={runAiMatch} disabled={aiBusy || !bulkText.trim() || board.masterCues.length === 0}
                  style={{ display: "flex", alignItems: "center", gap: 6, background: aiReview ? "none" : T.accent, border: aiReview ? `1px solid ${T.border}` : "none", borderRadius: 8, padding: "8px 14px", color: T.accentText, fontSize: 12.5, fontWeight: 800, cursor: aiBusy ? "default" : "pointer", opacity: aiBusy || !bulkText.trim() ? 0.6 : 1 }}>
                  {aiBusy ? <><Loader2 size={13} style={{ animation: "rb-spin 1s linear infinite" }} /> {t("aiMatchBusy")}</> : aiReview ? t("aiMatchRetry") : t("aiMatchRun")}
                </button>
              )}
              {!(bulkIsLane && bulkLaneMode === "ai" && !aiReview) && <button className="rb-btn" onClick={applyBulkPaste} disabled={bulkBusy || aiBusy || bulkParsed.length === 0 || (bulkIsLane && (bulkLaneMode === "ai" ? aiPlaced.length === 0 : bulkLanePlan.place.size === 0))}
                style={{ background: T.accent, border: "none", borderRadius: 8, padding: "8px 14px", color: T.accentText, fontSize: 12.5, fontWeight: 800, cursor: (bulkBusy || bulkParsed.length === 0) ? "default" : "pointer", opacity: (bulkBusy || bulkParsed.length === 0) ? 0.6 : 1 }}>
                {bulkBusy ? t("uploading") : bulkIsLane ? t("bulkLaneConfirmBtn") : (bulkPlan.add.length === 0 && bulkPlan.replace.size > 0 ? t("bulkPasteReplaceBtn") : t("bulkPasteConfirmBtn"))}
              </button>}
            </div>
        </Modal>
      )}

      {fcOpen && (
        <Modal title={t("fightCardTitle")} onClose={closeFightCard} closeLabel={t("cancel")} width={600}>
            <div style={{ fontSize: 11, color: T.subtext, marginBottom: 10, whiteSpace: "pre-line" }}>{t(fcUrlAvailable ? "fightCardHintBoth" : "fightCardHintImage")}</div>

            {!fcDead && (
              <>
                {fcUrlAvailable && (
                  <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                    {[["image", "fightCardModeImage"], ["url", "fightCardModeUrl"]].map(([mode, label]) => (
                      <button key={mode} className="rb-btn" onClick={() => { setFcMode(mode); setFcParsed(null); setFcError(""); }}
                        style={{
                          background: fcMode === mode ? T.accent : T.panel2,
                          color: fcMode === mode ? T.accentText : T.text,
                          border: `1px solid ${fcMode === mode ? T.accent : T.border}`,
                          borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer",
                        }}>
                        {t(label)}
                      </button>
                    ))}
                  </div>
                )}
                {fcMode === "url" && fcUrlAvailable ? (
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
                    <input aria-label={t("fightCardUrlPh")} className="rb-input" type="url" value={fcUrl} placeholder={t("fightCardUrlPh")}
                      onChange={(e) => { setFcUrl(e.target.value); setFcParsed(null); setFcError(""); }}
                      style={{ flex: "1 1 260px", fontSize: 12.5, border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 10px", background: "#fff", color: T.text }} />
                    <button className="rb-btn" onClick={scanFightCardUrl} disabled={fcBusy || !fcUrl.trim()}
                      style={{ background: T.accent, border: "none", borderRadius: 8, padding: "8px 14px", color: T.accentText, fontSize: 12.5, fontWeight: 800, cursor: (fcBusy || !fcUrl.trim()) ? "default" : "pointer", opacity: (fcBusy || !fcUrl.trim()) ? 0.6 : 1 }}>
                      {fcBusy ? t("fightCardScanning") : t("fightCardScanBtn")}
                    </button>
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
                    <label className="rb-btn" style={{ display: "flex", alignItems: "center", gap: 6, background: T.panel2, border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 12.5, fontWeight: 700, color: T.text, cursor: "pointer" }}>
                      <ImageIcon size={13} />
                      {fcFiles.length > 0 ? `${fcFiles.length} ${fcFiles.length === 1 ? "file" : "files"}` : t("fightCardPickBtn")}
                      <input type="file" accept={fcAccept} multiple hidden
                        onChange={(e) => { setFcFiles(Array.from(e.target.files || [])); setFcParsed(null); setFcError(""); }} />
                    </label>
                    <button className="rb-btn" onClick={scanFightCard} disabled={fcBusy || fcFiles.length === 0}
                      style={{ background: T.accent, border: "none", borderRadius: 8, padding: "8px 14px", color: T.accentText, fontSize: 12.5, fontWeight: 800, cursor: (fcBusy || fcFiles.length === 0) ? "default" : "pointer", opacity: (fcBusy || fcFiles.length === 0) ? 0.6 : 1 }}>
                      {fcBusy ? t("fightCardScanning") : t("fightCardScanBtn")}
                    </button>
                  </div>
                )}
              </>
            )}
            {fcError && (
              <div style={{ fontSize: 11.5, color: T.danger, background: T.dangerBg, borderRadius: 8, padding: "8px 10px", marginBottom: 10 }}>{fcError}</div>
            )}

            {fcMatched && fcMatched.length > 0 && (
              <>
                <div style={{ fontSize: 11, color: T.subtext, marginBottom: 8 }}>
                  {t("fightCardSummary").replace("{n}", fcMatched.length).replace("{m}", fcMatchedCount).replace("{k}", fcMatched.length - fcMatchedCount)}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
                  {fcMatched.map((row, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", background: T.panel2, border: `1px solid ${T.border}`, borderRadius: 8, padding: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 800, color: T.text, flex: "0 0 auto" }}>{t("boutPrefix")} {row.boutNumber}</span>
                      <input aria-label={t("redCornerPh")} className="rb-input" value={row.redCorner} placeholder={t("redCornerPh")}
                        onChange={(e) => updateFcRow(i, { redCorner: e.target.value })}
                        style={{ flex: "1 1 130px", fontSize: 11.5, border: `1px solid ${T.border}`, borderRadius: 6, padding: "5px 7px", background: "#fff", color: T.text }} />
                      <input aria-label={t("blueCornerPh")} className="rb-input" value={row.blueCorner} placeholder={t("blueCornerPh")}
                        onChange={(e) => updateFcRow(i, { blueCorner: e.target.value })}
                        style={{ flex: "1 1 130px", fontSize: 11.5, border: `1px solid ${T.border}`, borderRadius: 6, padding: "5px 7px", background: "#fff", color: T.text }} />
                      <span style={{ fontSize: 11, fontWeight: 700, color: row.cueId ? T.accent : T.subtext, flex: "1 1 140px" }}>
                        {row.cueId ? `${t("fightCardMatchedPrefix")} ${row.cueName}` : t("fightCardUnmatched")}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div style={{ display: "flex", gap: 8, marginTop: 4, justifyContent: "flex-end" }}>
              <button className="rb-btn" onClick={closeFightCard} disabled={fcBusy}
                style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 14px", color: T.text, fontSize: 12.5, fontWeight: 700, cursor: fcBusy ? "default" : "pointer" }}>
                {t("cancel")}
              </button>
              <button className="rb-btn" onClick={applyFightCard} disabled={fcBusy || fcMatchedCount === 0}
                style={{ background: T.accent, border: "none", borderRadius: 8, padding: "8px 14px", color: T.accentText, fontSize: 12.5, fontWeight: 800, cursor: (fcBusy || fcMatchedCount === 0) ? "default" : "pointer", opacity: (fcBusy || fcMatchedCount === 0) ? 0.6 : 1 }}>
                {t("fightCardApplyBtn")}
              </button>
            </div>
        </Modal>
      )}
    </div>
  );
}

// exact (normalized) name match only: a scene called "War" must not pick up
// the "New War" photo. Only fills a photo slot that is still empty.
async function presetPhotoPatch(stage, photoList, applyFromLibrary) {
  const find = (name) => { const n = normalizePhotoName(name); return n ? (photoList || []).find((p) => normalizePhotoName(p.name) === n) : null; };
  const patch = {};
  for (const [textF, bgF] of [["waikruText", "waikruBg"], ["duringFightText", "duringFightBg"]]) {
    if (stage[bgF]) continue;
    const m = find(stage[textF]);
    if (m) { try { patch[bgF] = await applyFromLibrary(m); } catch (e) {} }
  }
  return patch;
}

function StageRow({ stage, masterCues, onUpdate, onDelete }) {
  const { t } = useT();
  const { list: photoList, applyFromLibrary } = usePhotoLibrary();
  // "sync from template" is an action, not a stored field — it always shows the
  // placeholder, and choosing an option immediately copies that preset's text
  // onto this row (never its bg fields), then resets back to the placeholder.
  const handleSync = (e) => {
    const val = e.target.value;
    if (!val) return;
    const [cat, pos] = val.split("::");
    const p = findStagePreset(cat, pos);
    e.target.value = "";
    if (!p) return;
    const text = { stage: p.pos, waikruText: p.waikruText, duringFightText: p.duringFightText, sound: p.sound };
    // fill any EMPTY photo slot from the library (never replaces one), then
    // apply text + photos as ONE update (a second update would be built on a
    // stale board and undo the first)
    presetPhotoPatch({ ...stage, ...text }, photoList, applyFromLibrary).then((patch) => onUpdate({ ...text, ...patch }), () => onUpdate(text));
  };
  const range = stageRows(stage, masterCues);
  const linkedIds = range ? [] : stageCueIds(stage);
  const availableCues = masterCues.filter((m) => !linkedIds.includes(m.id));
  // picking either end of the range replaces any one-by-one links
  const setRangeEnd = (end, idx) => {
    if (idx === "") { onUpdate({ cueRange: null }); return; }
    const i = Number(idx);
    const from = end === "from" ? i : range ? range[0] : i;
    const to = end === "to" ? i : range ? range[1] : i;
    const [lo, hi] = from <= to ? [from, to] : [to, from];
    onUpdate({ cueRange: { from: masterCues[lo].id, to: masterCues[hi].id }, assignedCueIds: [] });
  };
  const cueOption = (m, i) => <option key={m.id} value={i}>{masterCueLabel(m, i, t)}</option>;
  const addLink = (e) => {
    const val = e.target.value;
    if (!val) return;
    onUpdate({ assignedCueIds: [...linkedIds, val] });
    e.target.value = "";
  };
  const removeLink = (cid) => onUpdate({ assignedCueIds: linkedIds.filter((x) => x !== cid) });
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", background: T.panel2, border: `1px solid ${T.border}`, borderRadius: 10, padding: 10, textAlign: "left" }}>
      <input aria-label={t("stageLabelPh")} className="rb-input" value={stage.stage} onChange={(e) => onUpdate({ stage: e.target.value })} placeholder={t("stageLabelPh")}
        style={{ width: 64, flex: "0 0 auto", fontSize: 12, fontWeight: 800, textAlign: "center", border: `1px solid ${T.border}`, borderRadius: 6, padding: "6px 4px", background: "#fff", color: T.text }} />
      <select aria-label={t("stageSyncLabel")} value="" onChange={handleSync} title={t("stageSyncLabel")}
        style={{ flex: "1 1 150px", fontSize: 11, border: `1px solid ${T.border}`, borderRadius: 6, padding: "6px 8px", background: "#fff", color: T.subtext }}>
        <option value="">{t("stageSyncLabel")}…</option>
        {STAGE_PRESET_CATEGORIES.map((cat) => (
          <optgroup key={cat} label={cat}>
            {(STAGE_PRESETS[cat] || []).map((p) => (
              <option key={`${cat}::${p.pos}`} value={`${cat}::${p.pos}`}>{cat} {p.pos} · {p.waikruText} / {p.duringFightText}</option>
            ))}
          </optgroup>
        ))}
      </select>
      <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: "1 1 170px" }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: T.subtext }}>{t("stageAssignLabel")}</span>
        <div data-stage-range style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center", fontSize: 11, color: T.subtext }}>
          <select aria-label={t("stageRangeFrom")} value={range ? range[0] : ""} onChange={(e) => setRangeEnd("from", e.target.value)}
            style={{ flex: "1 1 120px", minWidth: 0, fontSize: 11, border: `1px solid ${T.border}`, borderRadius: 6, padding: "5px 7px", background: "#fff", color: T.text }}>
            <option value="">{t("stageRangeFrom")}…</option>
            {masterCues.map(cueOption)}
          </select>
          <span>→</span>
          <select aria-label={t("stageRangeTo")} value={range ? range[1] : ""} onChange={(e) => setRangeEnd("to", e.target.value)}
            style={{ flex: "1 1 120px", minWidth: 0, fontSize: 11, border: `1px solid ${T.border}`, borderRadius: 6, padding: "5px 7px", background: "#fff", color: T.text }}>
            <option value="">{t("stageRangeTo")}…</option>
            {masterCues.map(cueOption)}
          </select>
          {range && <span style={{ fontWeight: 700, color: T.text }}>{t("stageRangeCount").replace("{n}", range[1] - range[0] + 1)}</span>}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {!range && linkedIds.length === 0 && <span style={{ fontSize: 11, color: T.subtext }}>{t("stageAssignNone")}</span>}
          {linkedIds.map((cid) => {
            const i = masterCues.findIndex((m) => m.id === cid);
            const cue = i === -1 ? null : masterCues[i];
            return (
              <span key={cid} style={{ display: "flex", alignItems: "center", gap: 4, background: "#fff", border: `1px solid ${T.border}`, borderRadius: 6, padding: "3px 6px", fontSize: 11, color: T.text, maxWidth: 220 }}>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cue ? masterCueLabel(cue, i, t) : cid}</span>
                <button aria-label={t("removeStageLinkBtn")} className="rb-btn" onClick={() => removeLink(cid)} title={t("removeStageLinkBtn")}
                  style={{ background: "none", border: "none", padding: 0, display: "flex", flex: "0 0 auto", cursor: "pointer", color: "inherit", opacity: 0.6 }}>
                  <X size={10} />
                </button>
              </span>
            );
          })}
        </div>
        {!range && availableCues.length > 0 && (
          <select aria-label={t("stageAssignAddBtn")} value="" onChange={addLink}
            style={{ fontSize: 11, border: `1px solid ${T.border}`, borderRadius: 6, padding: "5px 7px", background: "#fff", color: T.subtext }}>
            <option value="">{t("stageAssignAddBtn")}</option>
            {availableCues.map((m) => <option key={m.id} value={m.id}>{masterCueLabel(m, masterCues.findIndex((x) => x.id === m.id), t)}</option>)}
          </select>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: "1 1 170px" }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: T.subtext }}>{t("waikruLabel")}</span>
        <input aria-label={t("waikruLabel")} className="rb-input" value={stage.waikruText} onChange={(e) => onUpdate({ waikruText: e.target.value })}
          style={{ fontSize: 11.5, border: `1px solid ${T.border}`, borderRadius: 6, padding: "5px 7px", background: "#fff", color: T.text }} />
        <BgUploadButton bg={stage.waikruBg} onUpload={(id) => onUpdate({ waikruBg: id })}
          onRemove={() => onUpdate({ waikruBg: "" })}
          overlay="rgba(0,0,0,0.06)" textColor={T.text} suggestedName={stage.waikruText} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: "1 1 170px" }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: T.subtext }}>{t("duringFightLabel")}</span>
        <input aria-label={t("duringFightLabel")} className="rb-input" value={stage.duringFightText} onChange={(e) => onUpdate({ duringFightText: e.target.value })}
          style={{ fontSize: 11.5, border: `1px solid ${T.border}`, borderRadius: 6, padding: "5px 7px", background: "#fff", color: T.text }} />
        <BgUploadButton bg={stage.duringFightBg} onUpload={(id) => onUpdate({ duringFightBg: id })}
          onRemove={() => onUpdate({ duringFightBg: "" })}
          overlay="rgba(0,0,0,0.06)" textColor={T.text} suggestedName={stage.duringFightText} />
      </div>
      <input aria-label={t("stageSoundPh")} className="rb-input" value={stage.sound} onChange={(e) => onUpdate({ sound: e.target.value })} placeholder={t("stageSoundPh")}
        style={{ flex: "1 1 120px", fontSize: 11.5, border: `1px solid ${T.border}`, borderRadius: 6, padding: "6px 8px", background: "#fff", color: T.text }} />
      <button aria-label={t("deleteStageBtn")} className="rb-btn" onClick={onDelete} title={t("deleteStageBtn")}
        style={{ background: "none", border: "none", color: T.danger, cursor: "pointer", display: "flex", alignItems: "center", padding: 4 }}>
        <Trash2 size={14} />
      </button>
    </div>
  );
}

function ColumnChip({ label, visible, onToggle, onRemove }) {
  const { t } = useT();
  return (
    <span style={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: 4, background: visible ? T.panel2 : "transparent", color: visible ? T.text : "#5A5A5E", border: `1px solid ${T.border}`, borderRadius: 8, padding: "6px 10px", fontSize: 12, whiteSpace: "nowrap" }}>
      <button className="rb-btn" onClick={onToggle} title={visible ? t("hideColumn") : t("showColumn")} style={{ display: "flex", alignItems: "center", gap: 5, background: "none", border: "none", color: "inherit", cursor: "pointer", fontFamily: "inherit", fontSize: 12, padding: 0 }}>
        {visible ? <Eye size={12} /> : <EyeOff size={12} />} {label}
      </button>
      {onRemove && <button aria-label={t("removeColumnPerm")} className="rb-btn" onClick={onRemove} title={t("removeColumnPerm")} style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", opacity: 0.6, padding: 0, display: "flex" }}><X size={11} /></button>}
    </span>
  );
}

function BgUploadButton({ bg, onUpload, onRemove, overlay, textColor, suggestedName }) {
  const { t } = useT();
  const { list: libList, applyFromLibrary, lockToLibrary } = usePhotoLibrary();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [libOpen, setLibOpen] = useState(false);
  const [libQuery, setLibQuery] = useState("");
  const [lockOpen, setLockOpen] = useState(false);
  const [lockName, setLockName] = useState("");
  const [lockErr, setLockErr] = useState("");
  const [lockBusy, setLockBusy] = useState(false);

  const filteredLib = libList.filter((p) => normalizePhotoName(p.name).includes(normalizePhotoName(libQuery)));

  const pickFromLibrary = async (entry) => {
    setLibOpen(false);
    setBusy(true); setErr("");
    try { const id = await applyFromLibrary(entry); onUpload(id); }
    catch (e) { setErr(t("uploadFailed")); setTimeout(() => setErr(""), 2500); }
    setBusy(false);
  };

  const openLock = () => { setLockName(suggestedName || ""); setLockErr(""); setLockOpen(true); };
  const confirmLock = async () => {
    if (lockBusy) return;
    setLockBusy(true); setLockErr("");
    try { await lockToLibrary(lockName, bg); setLockOpen(false); setLockName(""); }
    catch (e) {
      setLockErr(e && e.message === "duplicate-name" ? t("photoNameDupError") : e && e.message === "empty-name" ? t("photoNameEmptyError") : t("uploadFailed"));
    }
    setLockBusy(false);
  };

  return (
    <div style={{ display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}>
      <label className="rb-btn" style={{ background: overlay, border: "none", borderRadius: 4, color: textColor, fontSize: 11, padding: "3px 8px", cursor: busy ? "default" : "pointer", display: "flex", alignItems: "center", gap: 4, fontWeight: 700, opacity: busy ? 0.6 : 1 }}>
        {busy ? t("uploading") : (bg ? t("changeBg") : t("addBg"))}
        <input type="file" accept="image/*" disabled={busy} style={{ display: "none" }} onChange={async (e) => {
          const f = e.target.files?.[0]; if (!f) return;
          setBusy(true); setErr("");
          try { const id = await uploadCueImage(f); onUpload(id); }
          catch (err2) { setErr(t("uploadFailed")); setTimeout(() => setErr(""), 2500); }
          setBusy(false);
          e.target.value = "";
        }} />
      </label>
      {bg && <button className="rb-btn" onClick={onRemove} style={{ background: overlay, border: "none", borderRadius: 4, color: textColor, fontSize: 11, padding: "3px 7px", cursor: "pointer", fontWeight: 700 }}>{t("removeImage")}</button>}

      <div style={{ position: "relative" }}>
        <button className="rb-btn" onClick={() => setLibOpen((v) => !v)} disabled={busy} title={t("pickFromLibraryHint")}
          style={{ background: overlay, border: "none", borderRadius: 4, color: textColor, fontSize: 11, padding: "3px 7px", cursor: busy ? "default" : "pointer", fontWeight: 700, display: "flex", alignItems: "center", gap: 3, opacity: busy ? 0.6 : 1 }}>
          <ImageIcon size={10} /> {t("pickFromLibraryBtn")}
        </button>
        {libOpen && (
          <>
            <div onClick={() => setLibOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 70 }} />
            <div onClick={(e) => e.stopPropagation()} style={{ position: "absolute", top: "calc(100% + 4px)", left: "50%", transform: "translateX(-50%)", background: "#1A1A1A", border: "1px solid #2C2C2C", borderRadius: 8, padding: 8, zIndex: 80, width: 210, boxShadow: "0 10px 28px rgba(0,0,0,0.45)" }}>
              <input aria-label={t("searchPhotosPh")} className="rb-input" autoFocus value={libQuery} onChange={(e) => setLibQuery(e.target.value)}
                placeholder={t("searchPhotosPh")} style={{ width: "100%", boxSizing: "border-box", fontSize: 11, border: "1px solid #333", borderRadius: 6, padding: "5px 7px", background: "#111", color: "#fff", marginBottom: 6 }} />
              <div style={{ maxHeight: 180, overflowY: "auto", display: "flex", flexDirection: "column", gap: 3 }}>
                {filteredLib.length === 0 && (
                  <div style={{ fontSize: 11, color: "#7A786F", textAlign: "center", padding: "8px 4px" }}>
                    {libList.length === 0 ? t("libraryPickerEmpty") : t("noPhotosFoundSearch")}
                  </div>
                )}
                {filteredLib.map((entry) => (
                  <button key={entry.id} className="rb-btn" onClick={() => pickFromLibrary(entry)}
                    style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", borderRadius: 5, padding: "5px 6px", cursor: "pointer", textAlign: "left", color: "#fff", fontSize: 11.5, fontWeight: 600 }}>
                    <span style={{ width: 22, height: 22, borderRadius: 4, background: "#333", backgroundImage: `url(${bgUrl(entry.bg)})`, backgroundSize: "cover", backgroundPosition: "center", flex: "0 0 auto" }} />
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{entry.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {bg && (
        <div style={{ position: "relative" }}>
          <button className="rb-btn" onClick={openLock} title={t("saveToLibraryHint")}
            style={{ background: overlay, border: "none", borderRadius: 4, color: textColor, fontSize: 11, padding: "3px 7px", cursor: "pointer", fontWeight: 700, display: "flex", alignItems: "center", gap: 3 }}>
            <Lock size={10} /> {t("saveToLibraryBtn")}
          </button>
          {lockOpen && (
            <>
              <div onClick={() => setLockOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 70 }} />
              <div onClick={(e) => e.stopPropagation()} style={{ position: "absolute", top: "calc(100% + 4px)", left: "50%", transform: "translateX(-50%)", background: "#1A1A1A", border: "1px solid #2C2C2C", borderRadius: 8, padding: 8, zIndex: 80, width: 190, boxShadow: "0 10px 28px rgba(0,0,0,0.45)" }}>
                <input aria-label={t("photoNamePh")} className="rb-input" autoFocus value={lockName} onChange={(e) => setLockName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") confirmLock(); if (e.key === "Escape") setLockOpen(false); }}
                  placeholder={t("photoNamePh")} style={{ width: "100%", boxSizing: "border-box", fontSize: 11, border: "1px solid #333", borderRadius: 6, padding: "5px 7px", background: "#111", color: "#fff", marginBottom: 5 }} />
                {lockErr && <div style={{ fontSize: 11, color: "#FFB4B0", marginBottom: 5 }}>{lockErr}</div>}
                <div style={{ display: "flex", gap: 4 }}>
                  <button className="rb-btn" onClick={confirmLock} disabled={lockBusy}
                    style={{ flex: 1, background: T.accent, border: "none", borderRadius: 5, color: "#161513", fontSize: 11, fontWeight: 800, padding: "5px 0", cursor: lockBusy ? "default" : "pointer", opacity: lockBusy ? 0.6 : 1 }}>
                    {lockBusy ? t("uploading") : t("saveToLibraryConfirm")}
                  </button>
                  <button className="rb-btn" onClick={() => setLockOpen(false)} style={{ background: "#2C2C2C", border: "none", borderRadius: 5, color: "#fff", fontSize: 11, padding: "5px 8px", cursor: "pointer" }}>{t("cancel")}</button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {err && <span style={{ fontSize: 11, color: "#FFB4B0" }}>{err}</span>}
    </div>
  );
}

function MasterBox({ cue, top, height, editable, selected, onToggleSelect, onReorder, onUpdate, onDelete, onCopy, onPasteRowAfter }) {
  const { t } = useT();
  const [editing, setEditing] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const c = colorOf(cue.color);
  // taller minimum than before: editing mode now also shows bout number + two
  // corner-name inputs, which need roughly two more input rows of headroom.
  const boxHeight = editing ? Math.max(height, 290) : height;
  const textColor = cue.bg ? "#FFFFFF" : c.text;
  const overlay = textColor === "#FFFFFF" ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.08)";
  const overlayStrong = textColor === "#FFFFFF" ? "rgba(255,255,255,0.32)" : "rgba(0,0,0,0.14)";
  const bgStyle = cue.bg
    ? { backgroundImage: `linear-gradient(rgba(0,0,0,0.32),rgba(0,0,0,0.65)), url(${bgUrl(cue.bg)})`, backgroundSize: "cover", backgroundPosition: "center" }
    : { background: c.fill };
  // click-and-drag reorder: the grip handle starts the drag, and the whole
  // box is a drop target — dropping another cue's handle onto this box moves
  // that cue to sit here (arbitrary distance, not just adjacent swap).
  const handleDragStart = (e) => {
    e.dataTransfer.setData("text/plain", cue.id);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOver(true); };
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const draggedId = e.dataTransfer.getData("text/plain");
    if (draggedId && draggedId !== cue.id) onReorder(draggedId, cue.id);
  };
  return (
    <div data-mastercue-id={cue.id} className="rb-cue" style={{ position: "absolute", top, height: boxHeight, left: 0, right: 0, padding: 4, boxSizing: "border-box", zIndex: editing ? 60 : 1, transition: "height 0.25s cubic-bezier(0.4,0,0.2,1)" }}
      onDragOver={editable ? handleDragOver : undefined} onDragLeave={editable ? () => setDragOver(false) : undefined} onDrop={editable ? handleDrop : undefined}>
      <div style={{
        height: "100%", borderRadius: 4, ...bgStyle, boxShadow: dragOver ? `0 0 0 2px ${T.text}` : selected ? `0 0 0 3px ${T.accent}` : "none",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 10px",
        position: "relative", color: textColor, textAlign: "center", overflowY: editing ? "auto" : "hidden",
      }}>
        {editable && (
          <div draggable onDragStart={handleDragStart} title={t("dragToReorder")} className="rb-ctl"
            style={{ position: "absolute", top: 6, left: 6, width: 20, height: 20, borderRadius: 4, background: overlay, color: textColor, cursor: "grab", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <GripVertical size={13} />
          </div>
        )}
        {onToggleSelect && !editing && (
          <input type="checkbox" aria-label={`${t("selectCue")}: ${cue.name || t("untitledCue")}`} checked={!!selected} data-select-cue className={selected ? "" : "rb-ctl rb-ctl-select"}
            onClick={(e) => { e.stopPropagation(); onToggleSelect(e.shiftKey); }} onChange={() => {}}
            style={{ position: "absolute", top: 8, left: editable ? 32 : 8, width: 16, height: 16, margin: 0, accentColor: T.accent, cursor: "pointer" }} />
        )}
        {!editing ? (
          <>
            <Icon id={cue.icon} size={22} color={textColor} />
            <div style={{ fontSize: 13.5, fontWeight: 800, lineHeight: 1.2 }}>{cue.name || t("untitledCue")}</div>
            {(cue.boutNumber || cue.redCorner || cue.blueCorner) && (
              <div style={{ fontSize: 11, opacity: 0.85, lineHeight: 1.3 }}>
                {cue.boutNumber && <>{t("boutPrefix")} {cue.boutNumber}{(cue.redCorner || cue.blueCorner) ? " · " : ""}</>}
                {(cue.redCorner || cue.blueCorner) && <>{cue.redCorner || "?"} vs {cue.blueCorner || "?"}</>}
              </div>
            )}
          </>
        ) : (
          <>
            <input aria-label={t("masterCueNamePh")} className="rb-input" value={cue.name} onChange={(e) => onUpdate({ name: e.target.value })} placeholder={t("masterCueNamePh")}
              style={{ width: "100%", textAlign: "center", fontSize: 13, fontWeight: 700, border: `1px solid ${overlayStrong}`, borderRadius: 4, padding: "5px 6px", background: overlay, color: textColor }} />
            <div style={{ display: "flex", gap: 3, width: "100%" }}>
              <input aria-label={t("boutNumberPh")} className="rb-input" value={cue.boutNumber || ""} onChange={(e) => onUpdate({ boutNumber: e.target.value })} placeholder={t("boutNumberPh")}
                style={{ width: 52, flex: "0 0 auto", textAlign: "center", fontSize: 11.5, border: `1px solid ${overlayStrong}`, borderRadius: 4, padding: "5px 4px", background: overlay, color: textColor }} />
              <input aria-label={t("redCornerPh")} className="rb-input" value={cue.redCorner || ""} onChange={(e) => onUpdate({ redCorner: e.target.value })} placeholder={t("redCornerPh")}
                style={{ flex: "1 1 0", minWidth: 0, textAlign: "center", fontSize: 11.5, border: `1px solid ${overlayStrong}`, borderRadius: 4, padding: "5px 4px", background: overlay, color: textColor }} />
            </div>
            <input aria-label={t("blueCornerPh")} className="rb-input" value={cue.blueCorner || ""} onChange={(e) => onUpdate({ blueCorner: e.target.value })} placeholder={t("blueCornerPh")}
              style={{ width: "100%", textAlign: "center", fontSize: 11.5, border: `1px solid ${overlayStrong}`, borderRadius: 4, padding: "5px 4px", background: overlay, color: textColor }} />
            <div style={{ display: "flex", gap: 3, flexWrap: "wrap", justifyContent: "center" }}>
              {ICONS.map((ic) => (
                <button aria-label={`${t("a11yCueIcon")}: ${ic}`} aria-pressed={cue.icon === ic} key={ic} className="rb-btn" onClick={() => onUpdate({ icon: ic })} style={{ background: cue.icon === ic ? overlayStrong : overlay, border: "none", borderRadius: 4, padding: 3, cursor: "pointer" }}>
                  <Icon id={ic} size={14} color={textColor} />
                </button>
              ))}
            </div>
            <ColorPicker value={cue.color} onChange={(id) => onUpdate({ color: id })} />
            <BgUploadButton bg={cue.bg} onUpload={(id) => onUpdate({ bg: id })} onRemove={() => onUpdate({ bg: "" })} overlay={overlay} textColor={textColor} suggestedName={cue.name} />
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "center" }}>
              {onPasteRowAfter && <button className="rb-btn" onClick={() => { onPasteRowAfter(); setEditing(false); }} style={{ background: overlay, border: "none", borderRadius: 4, color: textColor, fontSize: 11, padding: "3px 8px", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontWeight: 700 }}><ClipboardPaste size={11} /> {t("pasteRowAfter")}</button>}
              <button className="rb-btn" onClick={onDelete} style={{ background: overlay, border: "none", borderRadius: 4, color: textColor, fontSize: 11, padding: "3px 8px", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontWeight: 700 }}><Trash2 size={11} /> {t("deleteMasterCue")}</button>
            </div>
          </>
        )}
        {onCopy && !editing && <CopyButton className="rb-ctl" onCopy={onCopy} label={t("copyRow")} copiedLabel={t("copiedCue")} overlay={overlayStrong} textColor={textColor} />}
        {editable && (
          <button className={editing ? "rb-btn" : "rb-btn rb-ctl"} onClick={() => setEditing((v) => !v)} title={t("editToggle")}
            style={{ position: "absolute", bottom: 6, right: 6, width: 24, height: 24, borderRadius: 4, border: "none", background: overlayStrong, color: textColor, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {editing ? <Check size={13} /> : <Pencil size={12} />}
          </button>
        )}
      </div>
    </div>
  );
}

// small "copy" button in a cue's top-right corner; flashes a check when done
function CopyButton({ onCopy, label, copiedLabel, overlay, textColor, className = "" }) {
  const [done, setDone] = useState(false);
  const timer = useRef(null);
  const click = (e) => {
    e.stopPropagation();
    onCopy();
    setDone(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setDone(false), 1400);
  };
  return (
    <button className={`rb-btn ${done ? "" : className}`} onClick={click} aria-label={done ? copiedLabel : label} title={done ? copiedLabel : label} data-copy-cue
      style={{ position: "absolute", top: 6, right: 6, minWidth: 22, height: 22, padding: "0 5px", borderRadius: 4, border: "none", background: overlay, color: textColor, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 3, fontSize: 10.5, fontWeight: 700 }}>
      {done ? <><Check size={11} /> {copiedLabel}</> : <Copy size={11} />}
    </button>
  );
}

function ColorPicker({ value, onChange }) {
  return (
    <div style={{ display: "flex", gap: 3, flexWrap: "wrap", justifyContent: "center" }}>
      {COLORS.map((c) => (
        <button key={c.id} className="rb-btn" onClick={() => onChange(c.id)}
          style={{ width: 16, height: 16, borderRadius: 3, border: value === c.id ? "2px solid #fff" : "1px solid rgba(0,0,0,0.25)", background: c.fill, cursor: "pointer", padding: 0 }} />
      ))}
    </div>
  );
}

const isPoolDrag = (e) => { try { return Array.from(e.dataTransfer.types || []).includes(POOL_DRAG_TYPE); } catch (err) { return false; } };

// The cue pool as a compact strip above the board: pick a group, search, and
// drag a chip into any column slot (or onto a block to replace it). Clicking
// a chip copies it, so it can also be placed with the paste buttons.
function PoolPanel() {
  const { t } = useT();
  const items = useCuePool();
  const [open, setOpen] = useState(() => { try { return localStorage.getItem("rb:poolOpen") !== "0"; } catch (e) { return true; } });
  const [group, setGroup] = useState("");
  const [query, setQuery] = useState("");
  const toggle = () => setOpen((v) => { try { localStorage.setItem("rb:poolOpen", v ? "0" : "1"); } catch (e) {} return !v; });
  if (!items || !items.length) return null;
  const groups = groupsOf(items).filter((g) => items.some((i) => i.group === g));
  const q = normalizePhotoName(query);
  const shown = items.filter((i) => (!group || i.group === group) && (!q || normalizePhotoName(i.name).includes(q)));
  const chipBtn = (active) => ({ background: active ? T.accent : T.panel2, color: active ? T.accentText : T.text, border: `1px solid ${active ? T.accent : T.border}`, borderRadius: 14, padding: "3px 10px", fontSize: 11.5, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" });
  return (
    <section data-cue-pool aria-label={t("navCuePool")} style={{ maxWidth: 1100, margin: "0 auto 10px", background: T.panel, border: `1px solid ${T.border}`, borderRadius: 10, padding: open ? "8px 10px" : "4px 10px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <button className="rb-btn" onClick={toggle} aria-expanded={open} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", padding: 2, cursor: "pointer", color: T.text, fontWeight: 800, fontSize: 12.5 }}>
          <LayersIcon size={14} /> {t("navCuePool")} {open ? "▾" : "▸"}
        </button>
        {open && (
          <>
            <div className="rb-scroll" style={{ display: "flex", gap: 4, overflowX: "auto", flex: "1 1 300px" }}>
              <button className="rb-btn" onClick={() => setGroup("")} style={chipBtn(!group)}>{t("poolAll")}</button>
              {groups.map((g) => <button key={g} className="rb-btn" onClick={() => setGroup(g)} style={chipBtn(group === g)}>{g}</button>)}
            </div>
            <span style={{ display: "flex", alignItems: "center", gap: 4, background: T.panel2, border: `1px solid ${T.border}`, borderRadius: 14, padding: "2px 8px" }}>
              <SearchIcon size={12} color={T.subtext} />
              <input aria-label={t("poolSearch")} className="rb-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("poolSearch")}
                style={{ border: "none", outline: "none", background: "transparent", fontSize: 12, width: 120, color: T.text }} />
            </span>
          </>
        )}
      </div>
      {open && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 7, maxHeight: 96, overflowY: "auto" }}>
          {shown.map((it) => {
            const c = colorOf(it.color || "warmgray");
            return (
              <div key={it.id} data-pool-item={it.id} draggable role="button" tabIndex={0} title={`${it.group || ""} · ${t("poolChipHint")}`}
                onDragStart={(e) => { e.dataTransfer.setData(POOL_DRAG_TYPE, JSON.stringify({ name: it.name, sound: it.sound || "", color: it.color || "warmgray", photo: it.photo || "" })); e.dataTransfer.effectAllowed = "copy"; }}
                onClick={() => copyBlock({ image: it.name, sound: it.sound || "", color: it.color || "warmgray", bg: "" }, t("navCuePool"))}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); copyBlock({ image: it.name, sound: it.sound || "", color: it.color || "warmgray", bg: "" }, t("navCuePool")); } }}
                style={{ background: c.fill, color: c.text, borderRadius: 6, padding: "4px 9px", fontSize: 11.5, fontWeight: 700, cursor: "grab", userSelect: "none", maxWidth: 260, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {it.name}
              </div>
            );
          })}
          {!shown.length && <span style={{ fontSize: 12, color: T.subtext }}>{t("poolNoMatch")}</span>}
        </div>
      )}
    </section>
  );
}

function BlockColumn({ width, col, blocks, masterLen, heights, cum, editable, onAdd, onUpdate, onDelete, onSplit, onMove, onPoolDrop, canPaste, onPaste, onPasteInto, onCopy }) {
  const { t } = useT();
  const covered = coveredRows(blocks);
  const totalHeight = cum[cum.length - 1] || 0;
  const laid = layoutBlocks(blocks, heights, cum);
  const [dragOverRow, setDragOverRow] = useState(null);
  return (
    <div data-column={col} style={{ width, flex: `0 0 ${width}`, position: "relative", height: totalHeight }}>
      {Array.from({ length: masterLen }).map((_, i) => {
        if (covered.has(i)) return null;
        return (
          <div key={i} data-block-row={i} className="rb-row" style={{ position: "absolute", top: cum[i], height: heights[i], left: 0, right: 0, padding: 4, boxSizing: "border-box" }}
            onDragOver={editable ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = isPoolDrag(e) ? "copy" : "move"; setDragOverRow(i); } : undefined}
            onDragLeave={editable ? () => setDragOverRow((r) => (r === i ? null : r)) : undefined}
            onDrop={editable ? (e) => { e.preventDefault(); setDragOverRow(null); const pool = e.dataTransfer.getData(POOL_DRAG_TYPE); if (pool) { onPoolDrop && onPoolDrop(col, i, pool); return; } const draggedId = e.dataTransfer.getData("text/plain"); if (draggedId) onMove(col, draggedId, i); } : undefined}>
            {editable ? (
              <div style={{ display: "flex", gap: 4, width: "100%", height: "100%" }}>
                <button aria-label={t("a11yAddBlock")} className={dragOverRow === i ? "rb-btn" : "rb-btn rb-slot"} onClick={() => onAdd(col, i)}
                  style={{ flex: "1 1 0", height: "100%", border: `1.5px dashed ${dragOverRow === i ? T.text : T.border}`, borderRadius: 16, background: dragOverRow === i ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.03)", color: "#5A5A5E", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Plus size={16} />
                </button>
                {canPaste && (
                  <button aria-label={t("pasteHere")} title={t("pasteHere")} data-paste-slot={i} className="rb-btn rb-slot rb-slot-paste" onClick={() => onPaste(col, i)}
                    style={{ flex: "1 1 0", height: "100%", border: `1.5px dashed ${T.accentInk}`, borderRadius: 16, background: "rgba(255,90,31,0.06)", color: T.accentInk, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <ClipboardPaste size={16} />
                  </button>
                )}
              </div>
            ) : <div style={{ width: "100%", height: "100%", border: `1px dashed ${T.border}`, borderRadius: 16 }} />}
          </div>
        );
      })}
      {laid.map(({ block, top, height }) => (
        <Block key={block.id} block={block} top={top} height={height} editable={editable}
          onCopy={onCopy ? () => onCopy(block) : null} onPasteInto={editable && canPaste ? () => onPasteInto(col, block.id) : null}
          onUpdate={(patch) => onUpdate(col, block.id, patch)} onDelete={() => onDelete(col, block.id)} onSplit={() => onSplit(col, block.id)}
          onDragStartBlock={(e) => { e.dataTransfer.setData("text/plain", block.id); e.dataTransfer.effectAllowed = "move"; }}
          onDropBlock={(e) => { e.preventDefault(); const pool = e.dataTransfer.getData(POOL_DRAG_TYPE); if (pool) { onPoolDrop && onPoolDrop(col, block.start, pool, block.id); return; } const draggedId = e.dataTransfer.getData("text/plain"); if (draggedId && draggedId !== block.id) onMove(col, draggedId, block.start); }} />
      ))}
    </div>
  );
}

function Block({ block, top, height, editable, onUpdate, onDelete, onSplit, onDragStartBlock, onDropBlock, onCopy, onPasteInto }) {
  const { t } = useT();
  const [editing, setEditing] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const c = colorOf(block.color);
  const boxHeight = editing ? Math.max(height, 230) : height;
  const textColor = block.bg ? "#FFFFFF" : c.text;
  const overlay = textColor === "#FFFFFF" ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.08)";
  const overlayStrong = textColor === "#FFFFFF" ? "rgba(255,255,255,0.32)" : "rgba(0,0,0,0.14)";
  const bgStyle = block.bg
    ? { backgroundImage: `linear-gradient(rgba(0,0,0,0.32),rgba(0,0,0,0.65)), url(${bgUrl(block.bg)})`, backgroundSize: "cover", backgroundPosition: "center" }
    : { background: c.fill };
  return (
    <div data-block-id={block.id} className="rb-cue" style={{ position: "absolute", top, height: boxHeight, left: 0, right: 0, padding: 4, boxSizing: "border-box", zIndex: editing ? 60 : 1, transition: "height 0.25s cubic-bezier(0.4,0,0.2,1)" }}
      onDragOver={editable ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = isPoolDrag(e) ? "copy" : "move"; setDragOver(true); } : undefined}
      onDragLeave={editable ? () => setDragOver(false) : undefined}
      onDrop={editable ? (e) => { setDragOver(false); onDropBlock(e); } : undefined}>
      <div style={{
        height: "100%", borderRadius: 4, ...bgStyle, boxShadow: dragOver ? `0 0 0 2px ${T.text}` : "none",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, padding: "8px 10px",
        position: "relative", color: textColor, textAlign: "center", overflowY: editing ? "auto" : "hidden",
      }}>
        {editable && (
          <div draggable onDragStart={onDragStartBlock} title={t("dragToReorder")} className="rb-ctl"
            style={{ position: "absolute", top: 6, left: 6, width: 18, height: 18, borderRadius: 4, background: overlay, color: textColor, cursor: "grab", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <GripVertical size={12} />
          </div>
        )}
        {!editing ? (
          <>
            {block.sound && <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600, whiteSpace: "pre-line" }}>🔊 {block.sound}</div>}
            {block.image && <div style={{ fontSize: 12.5, fontWeight: 800, whiteSpace: "pre-line" }}>{block.image}</div>}
            {!block.sound && !block.image && <div style={{ fontSize: 11, opacity: 0.75 }}>{t("tapToFill")}</div>}
          </>
        ) : (
          <>
            <textarea aria-label={t("imagePh")} className="rb-input" rows={2} value={block.image} onChange={(e) => onUpdate({ image: e.target.value })} placeholder={t("imagePh")}
              style={{ width: "100%", textAlign: "center", fontSize: 12, fontWeight: 700, border: `1px solid ${overlayStrong}`, borderRadius: 4, padding: "4px 6px", background: overlay, color: textColor, resize: "vertical", fontFamily: "inherit" }} />
            <textarea aria-label={t("soundPh")} className="rb-input" rows={2} value={block.sound} onChange={(e) => onUpdate({ sound: e.target.value })} placeholder={t("soundPh")}
              style={{ width: "100%", textAlign: "center", fontSize: 11, border: `1px solid ${overlayStrong}`, borderRadius: 4, padding: "4px 6px", background: overlay, color: textColor, resize: "vertical", fontFamily: "inherit" }} />
            <ColorPicker value={block.color} onChange={(id) => onUpdate({ color: id })} />
            <BgUploadButton bg={block.bg} onUpload={(id) => onUpdate({ bg: id })} onRemove={() => onUpdate({ bg: "" })} overlay={overlay} textColor={textColor} />
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "center" }}>
              {onPasteInto && <button className="rb-btn" onClick={onPasteInto} style={{ background: overlay, border: "none", borderRadius: 4, color: textColor, fontSize: 11, padding: "3px 7px", cursor: "pointer", display: "flex", alignItems: "center", gap: 3, fontWeight: 700 }}><ClipboardPaste size={10} /> {t("pasteReplace")}</button>}
              <button className="rb-btn" onClick={onSplit} style={{ background: overlay, border: "none", borderRadius: 4, color: textColor, fontSize: 11, padding: "3px 7px", cursor: "pointer", display: "flex", alignItems: "center", gap: 3, fontWeight: 700 }}><Split size={10} /> {t("splitCue")}</button>
              <button className="rb-btn" onClick={onDelete} style={{ background: overlay, border: "none", borderRadius: 4, color: textColor, fontSize: 11, padding: "3px 7px", cursor: "pointer", display: "flex", alignItems: "center", gap: 3, fontWeight: 700 }}><Trash2 size={10} /> {t("deleteBtn")}</button>
            </div>
          </>
        )}
        {onCopy && !editing && (block.image || block.sound || block.bg) && <CopyButton className="rb-ctl" onCopy={onCopy} label={t("copyCue")} copiedLabel={t("copiedCue")} overlay={overlayStrong} textColor={textColor} />}
        {editable && (
          <button className={editing ? "rb-btn" : "rb-btn rb-ctl"} onClick={() => setEditing((v) => !v)} title={t("editToggle")}
            style={{ position: "absolute", bottom: 6, right: 6, width: 22, height: 22, borderRadius: 4, border: "none", background: overlayStrong, color: textColor, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {editing ? <Check size={12} /> : <Pencil size={11} />}
          </button>
        )}
      </div>
    </div>
  );
}

export { BoardEditor, StageRow, ColumnChip, BgUploadButton, MasterBox, ColorPicker, BlockColumn, Block };
